/**
 * Drag end utilities for handling element transfers between canvases
 * Extracted from Canvas.jsx to reduce file size
 */
import { clearTransformers } from "./transformerUtils";
import {
  getAbsoluteHorizontalExtent,
  getOffCanvasMainColumnBounds,
  getOffCanvasHorizontalStripSide,
  applyOffCanvasMainColumnAvoidanceX,
  shiftXToStayInOffCanvasStrip,
} from "./boundaryUtils";
import {
  getCanvasStageHeightPx,
  getStageHeightPxFromCanvasHeight,
} from "./mainCanvasLayout";

const TRANSFORMER_ANCHOR_BUFFER_PX = 10;
const EMPTY_TEXT_BOTTOM_CLAMP_SLACK_PX = 8;
const EMPTY_TEXT_TOP_CLAMP_SLACK_PX = 8;
const EMPTY_TEXT_HORIZONTAL_CLAMP_SLACK_PX = 8;
const EMPTY_TEXT_OFFCANVAS_MAIN_COLUMN_SLACK_PX = 10;
const TRANSFORMER_BUFFER_TYPES = new Set([
  "Rect",
  "Circle",
  "Image",
  "Line",
  "Arrow",
  "DoubleSidedArrow",
]);
const getTransformerEdgeInsetForType = (elementType) =>
  TRANSFORMER_BUFFER_TYPES.has(elementType)
    ? TRANSFORMER_ANCHOR_BUFFER_PX
    : 0;

const getEffectiveInset = (requestedInset, stageSize, renderedSize) => {
  const safeInset = Math.max(0, Number(requestedInset) || 0);
  const safeStage = Math.max(0, Number(stageSize) || 0);
  const safeRendered = Math.max(0, Number(renderedSize) || 0);
  return Math.max(0, Math.min(safeInset, (safeStage - safeRendered) / 2));
};

const getStageDimensions = (stage) => {
  const stageWidth = Number(stage?.width?.()) || 0;
  const stageHeight = Number(stage?.height?.()) || 0;
  const containerRect = stage?.container?.()?.getBoundingClientRect?.();
  const containerWidth = Number(containerRect?.width) || 0;
  const containerHeight = Number(containerRect?.height) || 0;

  return {
    width:
      stageWidth ||
      containerWidth ||
      window.innerWidth * 0.45,
    height:
      stageHeight ||
      containerHeight ||
      window.innerHeight,
  };
};

const getOffCanvasStageDimensions = (stage) => {
  const dims = getStageDimensions(stage);
  // Off-canvas stage height is design-fixed and should not shrink with main-canvas % height.
  // Keep a floor so drag-end clamps never use the main stage's reduced height.
  return {
    width: dims.width,
    height: Math.max(dims.height, getCanvasStageHeightPx()),
  };
};

const isEffectivelyEmptyText = (textValue) => {
  const raw = textValue == null ? "" : String(textValue);
  const withoutTags = raw.replace(/<[^>]*>/g, "");
  return withoutTags.trim().length === 0;
};

/**
 * Helper function to calculate bounding box for constraint purposes
 * Simplified version that doesn't account for rotation (for performance)
 */
export const getSimpleBoundingBox = (element, elementType) => {
  switch (elementType) {
    case "Rect": {
      return {
        width: element.width || 0,
        height: element.height || 0,
        isCenterCoordinate: false,
      };
    }
    case "Circle": {
      const fallbackRadius =
        element.radius ||
        Math.max(element.radiusX || 0, element.radiusY || 0) ||
        0;
      const radiusX =
        element.radiusX !== undefined && element.radiusX !== null
          ? element.radiusX
          : fallbackRadius;
      const radiusY =
        element.radiusY !== undefined && element.radiusY !== null
          ? element.radiusY
          : fallbackRadius;
      return {
        width: Math.max(0, radiusX * 2),
        height: Math.max(0, radiusY * 2),
        isCenterCoordinate: true,
      };
    }
    case "Image": {
      // Images can use either center or top-left coordinates
      const useTopLeftAnchor = element.useTopLeftAnchor === true;
      return {
        width: element.width || 0,
        height: element.height || 0,
        isCenterCoordinate: !useTopLeftAnchor,
      };
    }
    case "Text": {
      return {
        width: element.width || 0,
        height: element.height || 0,
        isCenterCoordinate: false,
      };
    }
    case "Line":
    case "Arrow":
    case "DoubleSidedArrow": {
      // For lines/arrows, calculate bounding box from points
      const points = element.points || [0, 0, 0, 0];
      if (points.length >= 4) {
        const xCoords = points.filter((_, i) => i % 2 === 0);
        const yCoords = points.filter((_, i) => i % 2 === 1);
        const minX = Math.min(...xCoords);
        const maxX = Math.max(...xCoords);
        const minY = Math.min(...yCoords);
        const maxY = Math.max(...yCoords);
        // Add padding for stroke width
        const strokeWidth = element.strokeWidth || 2;
        const padding = Math.max(strokeWidth / 2, 5);
        return {
          width: (maxX - minX) + padding * 2,
          height: (maxY - minY) + padding * 2,
          isCenterCoordinate: false,
        };
      }
      return {
        width: 0,
        height: 0,
        isCenterCoordinate: false,
      };
    }
    default:
      return {
        width: 0,
        height: 0,
        isCenterCoordinate: false,
      };
  }
};

/**
 * Constrains position for off-canvas elements to prevent negative x and going off screen
 */
/**
 * @param {string|null|undefined} lockedHorizontalStripSide - If "left" | "right", lock to that off-canvas strip for this call (e.g. drag-start side). If null/undefined, derive from element bbox (drag-end / one-shot).
 */
export const constrainOffCanvasPosition = (
  element,
  elementType,
  newX,
  newY,
  offCanvasStage,
  lockedHorizontalStripSide = null,
  edgeInsetPx = 0,
) => {
  if (!offCanvasStage || !element) {
    return { constrainedX: newX, constrainedY: newY };
  }

  const { width: stageWidth, height: stageHeight } =
    getOffCanvasStageDimensions(offCanvasStage);

  let lockedSide = lockedHorizontalStripSide;
  if (lockedSide == null) {
    const origExt = getAbsoluteHorizontalExtent(element, elementType, element.x);
    lockedSide = getOffCanvasHorizontalStripSide(
      origExt.minX,
      origExt.maxX,
      stageWidth,
    );
  }

  // Special handling for lines/arrows - need to constrain based on actual point positions
  if (elementType === "Line" || elementType === "Arrow" || elementType === "DoubleSidedArrow") {
    const points = element.points || [0, 0, 0, 0];
    if (points.length >= 4) {
      // Calculate bounding box from relative points
      let minPointX = Infinity, minPointY = Infinity;
      let maxPointX = -Infinity, maxPointY = -Infinity;

      for (let i = 0; i < points.length; i += 2) {
        minPointX = Math.min(minPointX, points[i]);
        maxPointX = Math.max(maxPointX, points[i]);
        minPointY = Math.min(minPointY, points[i + 1]);
        maxPointY = Math.max(maxPointY, points[i + 1]);
      }

      // Calculate absolute positions with current group position
      const absMinX = minPointX + newX;
      const absMaxX = maxPointX + newX;
      const absMinY = minPointY + newY;
      const absMaxY = maxPointY + newY;

      // Add padding for stroke width
      const strokeWidth = element.strokeWidth || 2;
      const padding = Math.max(strokeWidth / 2, 5);
      const insetX = getEffectiveInset(
        edgeInsetPx,
        stageWidth,
        Math.max(0, absMaxX - absMinX),
      );
      const insetY = getEffectiveInset(
        edgeInsetPx,
        stageHeight,
        Math.max(0, absMaxY - absMinY),
      );
      const minAllowedX = padding + insetX;
      const maxAllowedX = stageWidth - padding - insetX;
      const minAllowedY = padding + insetY;
      const maxAllowedY = stageHeight - padding - insetY;

      // Calculate how much we need to adjust the group position
      let deltaX = 0;
      let deltaY = 0;

      // Constrain X: ensure all points stay within bounds
      if (absMinX < minAllowedX) {
        deltaX = minAllowedX - absMinX; // Move right
      } else if (absMaxX > maxAllowedX) {
        deltaX = maxAllowedX - absMaxX; // Move left
      }

      // Constrain Y: ensure all points stay within bounds
      if (absMinY < minAllowedY) {
        deltaY = minAllowedY - absMinY; // Move down
      } else if (absMaxY > maxAllowedY) {
        deltaY = maxAllowedY - absMaxY; // Move up
      }

      let cx = newX + deltaX;
      let cy = newY + deltaY;
      cx = shiftXToStayInOffCanvasStrip(
        element,
        elementType,
        cx,
        stageWidth,
        lockedSide,
      );

      // Re-apply horizontal padding (one pass) after strip lock
      const absMinX2 = minPointX + cx;
      const absMaxX2 = maxPointX + cx;
      let deltaX2 = 0;
      if (absMinX2 < minAllowedX) {
        deltaX2 = minAllowedX - absMinX2;
      } else if (absMaxX2 > maxAllowedX) {
        deltaX2 = maxAllowedX - absMaxX2;
      }
      cx += deltaX2;
      cx = shiftXToStayInOffCanvasStrip(
        element,
        elementType,
        cx,
        stageWidth,
        lockedSide,
      );

      // Final stage-edge clamp after the last strip-lock.
      // shiftXToStayInOffCanvasStrip can move elements closer to the stage edge
      // (it doesn't know about edgeInsetPx), so we re-apply the buffered bounds
      // to keep transformer anchors fully visible, especially on the right side.
      const absMinX3 = minPointX + cx;
      const absMaxX3 = maxPointX + cx;
      let deltaX3 = 0;
      if (absMinX3 < minAllowedX) {
        deltaX3 = minAllowedX - absMinX3;
      } else if (absMaxX3 > maxAllowedX) {
        deltaX3 = maxAllowedX - absMaxX3;
      }
      cx += deltaX3;

      // Also keep buffered distance from the off-canvas "main column" seam.
      // shiftXToStayInOffCanvasStrip prevents crossing the seam but doesn't account
      // for edgeInsetPx, so the transformer anchors can still get clipped on the right.
      cx = applyOffCanvasMainColumnAvoidanceX(
        element,
        elementType,
        cx,
        cy,
        stageWidth,
        edgeInsetPx,
      ).x;

      // Re-apply stage-edge clamp after seam avoidance.
      const absMinX4 = minPointX + cx;
      const absMaxX4 = maxPointX + cx;
      let deltaX4 = 0;
      if (absMinX4 < minAllowedX) {
        deltaX4 = minAllowedX - absMinX4;
      } else if (absMaxX4 > maxAllowedX) {
        deltaX4 = maxAllowedX - absMaxX4;
      }
      cx += deltaX4;

      return {
        constrainedX: cx,
        constrainedY: cy,
      };
    }
  }

  // For other element types, use bounding box approach
  let bbox = getSimpleBoundingBox(element, elementType);

  let constrainedX = newX;
  let constrainedY = newY;

  // For images, calculate rotation-aware bounding box dimensions
  if (elementType === "Image" && element.rotation) {
    const rotation = Number(element.rotation) || 0;
    const theta = (rotation * Math.PI) / 180;
    const absCos = Math.abs(Math.cos(theta));
    const absSin = Math.abs(Math.sin(theta));
    
    // Calculate rotated bounding box dimensions
    const rotatedWidth = bbox.width * absCos + bbox.height * absSin;
    const rotatedHeight = bbox.width * absSin + bbox.height * absCos;
    
    // Create rotation-aware bbox
    bbox = {
      width: rotatedWidth,
      height: rotatedHeight,
      isCenterCoordinate: bbox.isCenterCoordinate
    };
  }

  // DEBUG: Log bbox dimensions and rotation to identify the issue
  console.log("DEBUG: constrainOffCanvasPosition bbox calculation", {
    elementType: elementType,
    bbox: bbox,
    elementRotation: element.rotation,
    elementWidth: element.width,
    elementHeight: element.height,
    newX: newX,
    newY: newY,
    stageWidth: stageWidth,
    stageHeight: stageHeight
  });

  if (bbox.isCenterCoordinate) {
    // For center coordinate elements (Image, Circle)
    const halfWidth = bbox.width / 2;
    const halfHeight = bbox.height / 2;
    const insetX = getEffectiveInset(edgeInsetPx, stageWidth, bbox.width);
    const insetY = getEffectiveInset(edgeInsetPx, stageHeight, bbox.height);
    
    const minX = halfWidth + insetX;
    const maxX = stageWidth - halfWidth - insetX;
    const originalConstrainedX = Math.max(minX, Math.min(maxX, newX));
    
    console.log("DEBUG: constrainOffCanvasPosition center coordinate calculation", {
      halfWidth: halfWidth,
      halfHeight: halfHeight,
      insetX: insetX,
      insetY: insetY,
      minX: minX,
      maxX: maxX,
      originalConstrainedX: originalConstrainedX,
      elementRotation: element.rotation
    });
    
    constrainedX = originalConstrainedX;
    constrainedY = Math.max(
      halfHeight + insetY,
      Math.min(stageHeight - halfHeight - insetY, newY)
    );
  } else {
    // For top-left coordinate elements (Rect, Text)
    const insetX = getEffectiveInset(edgeInsetPx, stageWidth, bbox.width);
    const insetY = getEffectiveInset(edgeInsetPx, stageHeight, bbox.height);
    constrainedX = Math.max(
      insetX,
      Math.min(stageWidth - bbox.width - insetX, newX),
    );
    constrainedY = Math.max(
      insetY,
      Math.min(stageHeight - bbox.height - insetY, newY),
    );
  }

  const beforeShiftX = constrainedX;
  constrainedX = shiftXToStayInOffCanvasStrip(
    element,
    elementType,
    constrainedX,
    stageWidth,
    lockedSide,
  );
  
  console.log("DEBUG: shiftXToStayInOffCanvasStrip result", {
    elementType: elementType,
    beforeShiftX: beforeShiftX,
    afterShiftX: constrainedX,
    shifted: beforeShiftX !== constrainedX,
    lockedSide: lockedSide,
    elementRotation: element.rotation
  });

  // Final stage-edge clamp after the last strip-lock.
  // shiftXToStayInOffCanvasStrip uses stage boundaries without the transformer
  // buffer inset, so without this re-clamp right-side resizing can clip anchors.
  const insetXFinal = getEffectiveInset(edgeInsetPx, stageWidth, bbox.width);
  if (bbox.isCenterCoordinate) {
    const halfWidth = bbox.width / 2;
    constrainedX = Math.max(
      halfWidth + insetXFinal,
      Math.min(stageWidth - halfWidth - insetXFinal, constrainedX),
    );
  } else {
    constrainedX = Math.max(
      insetXFinal,
      Math.min(stageWidth - bbox.width - insetXFinal, constrainedX),
    );
  }

  // Also keep buffered distance away from the off-canvas main-column seam.
  constrainedX = applyOffCanvasMainColumnAvoidanceX(
    element,
    elementType,
    constrainedX,
    constrainedY,
    stageWidth,
    edgeInsetPx,
  ).x;

  // Re-apply stage-edge clamp after seam avoidance.
  if (bbox.isCenterCoordinate) {
    const halfWidth = bbox.width / 2;
    constrainedX = Math.max(
      halfWidth + insetXFinal,
      Math.min(stageWidth - halfWidth - insetXFinal, constrainedX),
    );
  } else {
    constrainedX = Math.max(
      insetXFinal,
      Math.min(stageWidth - bbox.width - insetXFinal, constrainedX),
    );
  }

  return { constrainedX, constrainedY };
};

/**
 * Calculates client coordinates for boundary detection based on element type
 */
export const calculateClientCoordinates = ({
  type,
  draggedElement,
  newX,
  newY,
  sourceBBox,
}) => {
  let clientX, clientY;

  if (type === "Line" || type === "Arrow" || type === "DoubleSidedArrow") {
    // For lines and arrows, calculate the center point of the element
    const points = draggedElement.points || [0, 0, 0, 0];

    // Calculate the bounds of the element
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    for (let i = 0; i < points.length; i += 2) {
      const absX = newX + points[i]; // Convert relative to absolute
      const absY = newY + points[i + 1];
      minX = Math.min(minX, absX);
      maxX = Math.max(maxX, absX);
      minY = Math.min(minY, absY);
      maxY = Math.max(maxY, absY);
    }

    // Use the center of the element for boundary detection
    const centerX = (minX + maxX) / 2;
    const centerY = (minY + maxY) / 2;

    clientX = sourceBBox.left + centerX;
    clientY = sourceBBox.top + centerY;
  } else if (type === "Circle") {
    // Circles use center-based coordinates, so newX/newY is already the center
    clientX = sourceBBox.left + newX;
    clientY = sourceBBox.top + newY;
  } else if (type === "Rect") {
    // For rectangles, treat newX/newY as the top-left origin. This keeps boundary
    // detection and cross-canvas logic consistent with how rectangles are stored
    // in state (top-left anchored), and avoids double-applying center offsets.
    clientX = sourceBBox.left + newX;
    clientY = sourceBBox.top + newY;
  } else if (type === "Image") {
    // Images can use either center or top-left coordinates depending on useTopLeftAnchor
    const useTopLeftAnchor = draggedElement.useTopLeftAnchor === true;
    if (useTopLeftAnchor) {
      // Top-left anchor: calculate center
      const width = draggedElement.width || 0;
      const height = draggedElement.height || 0;
      const centerX = newX + width / 2;
      const centerY = newY + height / 2;
      clientX = sourceBBox.left + centerX;
      clientY = sourceBBox.top + centerY;
    } else {
      // Center anchor: newX/newY is already the center
      clientX = sourceBBox.left + newX;
      clientY = sourceBBox.top + newY;
    }
  } else if (type === "Text") {
    // CRITICAL FIX: For text, calculate the center point for consistent boundary detection
    // Text uses top-left coordinates, so we need to add half width/height to get center
    const width = draggedElement.width || 0;
    const height = draggedElement.height || 0;
    const centerX = newX + width / 2;
    const centerY = newY + height / 2;
    clientX = sourceBBox.left + centerX;
    clientY = sourceBBox.top + centerY;
  } else {
    // Default: use coordinates as-is (fallback for unknown types)
    clientX = sourceBBox.left + newX;
    clientY = sourceBBox.top + newY;
  }

  return { clientX, clientY };
};

/**
 * Clamp by rendered client-rect (rotation/stroke-aware) instead of model width/height.
 * This avoids cases where x=0 still leaves part of a rotated rectangle outside stage.
 */
const clampWithRenderedClientRect = ({
  stage,
  node,
  candidateX,
  candidateY,
  avoidOffCanvasMainColumn = false,
  edgeInsetPx = 0,
  horizontalSlackPx = 0,
  topSlackPx = 0,
  bottomSlackPx = 0,
  offCanvasMainColumnSlackPx = 0,
  roundResult = true,
}) => {
  const isRectDragDebugEnabled = (() => {
    try {
      return (
        typeof window !== "undefined" &&
        window.localStorage?.getItem("debugRectDragEndX") === "1"
      );
    } catch {
      return false;
    }
  })();
  if (!stage || !node) {
    return { x: candidateX, y: candidateY };
  }
  const { width: stageWidth, height: rawStageHeight } = getStageDimensions(stage);
  const stageHeight = avoidOffCanvasMainColumn
    ? Math.max(rawStageHeight, getCanvasStageHeightPx())
    : rawStageHeight;
  const containerRect = stage?.container?.()?.getBoundingClientRect?.();

  const prevX = node.x();
  const prevY = node.y();
  try {
    node.x(candidateX);
    node.y(candidateY);
    const { mainZoneStart, mainZoneEnd } = avoidOffCanvasMainColumn
      ? getOffCanvasMainColumnBounds(stageWidth)
      : { mainZoneStart: 0, mainZoneEnd: 0 };

    for (let pass = 0; pass < 8; pass += 1) {
      const rect = node.getClientRect();
      const insetX = getEffectiveInset(edgeInsetPx, stageWidth, rect.width);
      const insetY = getEffectiveInset(edgeInsetPx, stageHeight, rect.height);
      let dx = 0;
      let dy = 0;

      if (rect.x < insetX - horizontalSlackPx) {
        dx = insetX - horizontalSlackPx - rect.x;
      } else if (
        rect.x + rect.width >
        stageWidth - insetX + horizontalSlackPx
      ) {
        dx =
          stageWidth - insetX + horizontalSlackPx - (rect.x + rect.width);
      }
      if (rect.y < insetY - topSlackPx) {
        dy = insetY - topSlackPx - rect.y;
      }
      else if (rect.y + rect.height > stageHeight - insetY + bottomSlackPx) {
        dy = stageHeight - insetY + bottomSlackPx - (rect.y + rect.height);
      }

      if (avoidOffCanvasMainColumn) {
        const seamSlack = Math.max(0, Number(offCanvasMainColumnSlackPx) || 0);
        const effectiveMainZoneStart = mainZoneStart + seamSlack;
        const effectiveMainZoneEnd = mainZoneEnd - seamSlack;
        const seamInsetX = Math.max(0, Number(insetX) || 0);
        const hasRightStrip = mainZoneEnd < stageWidth - 1e-3;
        const overlapsMainColumn =
          rect.x < effectiveMainZoneEnd &&
          rect.x + rect.width > effectiveMainZoneStart;
        if (overlapsMainColumn) {
          if (!hasRightStrip) {
            // Current layout: off-canvas exists only on the left side.
            // Never push into a non-existent right strip.
            // Keep transformer anchor buffer from the seam.
            dx += effectiveMainZoneStart - seamInsetX - (rect.x + rect.width);
          } else {
            const centerX = rect.x + rect.width / 2;
            const mid = (effectiveMainZoneStart + effectiveMainZoneEnd) / 2;
            if (centerX < mid) {
              dx +=
                effectiveMainZoneStart - seamInsetX - (rect.x + rect.width);
            } else {
              dx += effectiveMainZoneEnd + seamInsetX - rect.x;
            }
          }
        }
      }

      if (Math.abs(dx) < 0.01 && Math.abs(dy) < 0.01) {
        break;
      }

      node.x((Number(node.x()) || 0) + dx);
      node.y((Number(node.y()) || 0) + dy);
    }

    const rawX = Number(node.x()) || 0;
    const rawY = Number(node.y()) || 0;
    let nextX = roundResult ? Math.round(rawX) : rawX;
    let nextY = roundResult ? Math.round(rawY) : rawY;
    node.x(nextX);
    node.y(nextY);

    // Integer rounding can move the rendered client-rect 1px across bounds
    // (seen with very wide rectangles near the left edge). Re-check bounds and
    // apply an integer correction so drag-end remains consistent with inset rules.
    if (roundResult) {
      const rectAfterRound = node.getClientRect();
      const insetX = getEffectiveInset(edgeInsetPx, stageWidth, rectAfterRound.width);
      const insetY = getEffectiveInset(edgeInsetPx, stageHeight, rectAfterRound.height);
      let snapDx = 0;
      let snapDy = 0;

      if (rectAfterRound.x < insetX - horizontalSlackPx) {
        snapDx = Math.ceil(insetX - horizontalSlackPx - rectAfterRound.x);
      } else if (
        rectAfterRound.x + rectAfterRound.width >
        stageWidth - insetX + horizontalSlackPx
      ) {
        snapDx = -Math.ceil(
          rectAfterRound.x +
            rectAfterRound.width -
            (stageWidth - insetX + horizontalSlackPx),
        );
      }

      if (rectAfterRound.y < insetY - topSlackPx) {
        snapDy = Math.ceil(insetY - topSlackPx - rectAfterRound.y);
      } else if (
        rectAfterRound.y + rectAfterRound.height >
        stageHeight - insetY + bottomSlackPx
      ) {
        snapDy = -Math.ceil(
          rectAfterRound.y +
            rectAfterRound.height -
            (stageHeight - insetY + bottomSlackPx),
        );
      }

      if (snapDx !== 0 || snapDy !== 0) {
        nextX += snapDx;
        nextY += snapDy;
        node.x(nextX);
        node.y(nextY);
      }
      if (isRectDragDebugEnabled) {
        const rectAfterSnap = node.getClientRect();
        console.log("[RectDragDebug][clampWithRenderedClientRect]", {
          candidateX,
          candidateY,
          rawX,
          rawY,
          roundedX: roundResult ? Math.round(rawX) : rawX,
          roundedY: roundResult ? Math.round(rawY) : rawY,
          finalX: nextX,
          finalY: nextY,
          snapDx,
          snapDy,
          rectAfterRound: rectAfterRound,
          rectAfterSnap,
          stageWidth,
          stageHeight,
          edgeInsetPx,
          horizontalSlackPx,
          topSlackPx,
          bottomSlackPx,
          avoidOffCanvasMainColumn,
        });
      }
    }
    node.getLayer?.()?.batchDraw?.();
    if (avoidOffCanvasMainColumn) {
    }
    return { x: nextX, y: nextY };
  } catch (_e) {
    node.x(prevX);
    node.y(prevY);
    return { x: candidateX, y: candidateY };
  }
};

/**
 * Determines which canvas (main or offCanvas) an element should be placed in
 */
export const determineTargetCanvas = ({
  type,
  draggedElement,
  clientX,
  clientY,
  mainBBox,
  offBBox,
  mainCanvasRight,
  mainCanvasBottom,
  sourceStageRef,
  mainStage,
  offCanvasStage,
  isFromMainCanvas, // NEW: indicates if element originally belonged to main canvas
}) => {
  // Product rule: never allow cross-stage transfers.
  // Main-canvas elements must remain on main; off-canvas elements must remain off-canvas.
  if (isFromMainCanvas) {
    return "main";
  }
  return "offCanvas";

  let target = null;

  if (target === null) {
    // Element is from off-canvas - use original logic to determine target
    // For arrows with multiple waypoints, use a much simpler and more aggressive approach
    if (type === "Arrow" || type === "Line" || type === "DoubleSidedArrow") {
      const points = draggedElement.points || [0, 0, 0, 0];
      const hasMultipleWaypoints = points.length > 4;

      if (hasMultipleWaypoints) {
        // For complex arrows with waypoints, consider the source stage and movement direction
        const isInMainCanvas =
          clientX >= mainBBox.left &&
          clientX <= mainCanvasRight &&
          clientY >= mainBBox.top &&
          clientY <= mainCanvasBottom;

        const isInOffCanvas =
          clientX >= offBBox.left &&
          clientX <= offBBox.right &&
          clientY >= offBBox.top &&
          clientY <= offBBox.bottom;

        // If the arrow is clearly in one canvas, use that as the target
        if (isInMainCanvas) {
          target = "main";
        } else if (isInOffCanvas) {
          target = "offCanvas";
        } else {
          // If the arrow is in the boundary area, use source stage to determine direction
          const isMovingTowardsOffCanvas =
            clientX > mainCanvasRight - 100 ||
            clientY > mainCanvasBottom - 100 ||
            clientX < mainBBox.left + 100 ||
            clientY < mainBBox.top + 100;

          if (sourceStageRef === mainStage && isMovingTowardsOffCanvas) {
            target = "offCanvas";
          } else if (
            sourceStageRef === offCanvasStage &&
            !isMovingTowardsOffCanvas
          ) {
            target = "main";
          } else {
            // Default to source stage if unclear
            target = sourceStageRef === mainStage ? "main" : "offCanvas";
          }
        }
      } else {
        // For simple arrows, use the original center-based logic
        if (
          clientX >= mainBBox.left &&
          clientX <= mainCanvasRight &&
          clientY >= mainBBox.top &&
          clientY <= mainCanvasBottom
        ) {
          target = "main";
        } else if (
          clientX >= offBBox.left &&
          clientX <= offBBox.right &&
          clientY >= offBBox.top &&
          clientY <= offBBox.bottom
        ) {
          target = "offCanvas";
        }
      }
    } else {
      // For non-arrow elements, use the original logic
      // Check if element is clearly within main canvas bounds (including vertical bounds)
      const isInMainCanvas =
        clientX >= mainBBox.left &&
        clientX <= mainCanvasRight &&
        clientY >= mainBBox.top &&
        clientY <= mainCanvasBottom;

      // Check if element is in off-canvas area
      const isInOffCanvasArea =
        clientX >= offBBox.left &&
        clientX <= offBBox.right &&
        clientY >= offBBox.top &&
        clientY <= offBBox.bottom;

      // Check if element is below the main canvas (in the space under the main canvas stage)
      const isBelowMainCanvas = clientY > mainCanvasBottom;

      // For elements from off-canvas:
      // Priority order:
      // 1. If below main canvas (even if horizontally within bounds), ALWAYS keep in off-canvas (highest priority)
      // 2. If clearly in main canvas bounds (and NOT below), move to main
      // 3. If in off-canvas area, keep in off-canvas
      // 4. Otherwise, default to source stage

      // CRITICAL: Check isBelowMainCanvas FIRST for off-canvas elements
      // This prevents elements from being incorrectly moved to main canvas when dragged below
      if (isBelowMainCanvas && sourceStageRef === offCanvasStage) {
        // Element from off-canvas dragged below main canvas - always keep in off-canvas
        target = "offCanvas";
      } else if (isInMainCanvas) {
        // Only move to main if clearly within bounds AND not below main canvas
        target = "main";
      } else if (isInOffCanvasArea) {
        target = "offCanvas";
      } else if (isBelowMainCanvas) {
        // Element dragged below main canvas but from main canvas - keep in main
        // (This case is handled by the isFromMainCanvas branch above, but adding for safety)
        target = sourceStageRef === mainStage ? "main" : "offCanvas";
      }
    }
  }

  // If we can't determine the target, default based on source
  if (target === null) {
    // If from main canvas, default to main; otherwise use source stage
    if (isFromMainCanvas) {
      target = "main";
    } else {
      target = sourceStageRef === mainStage ? "main" : "offCanvas";
    }
  }

  // Simple fallback for arrows with waypoints if target is still null
  if (
    (type === "Arrow" || type === "Line" || type === "DoubleSidedArrow") &&
    target === null
  ) {
    const points = draggedElement.points || [0, 0, 0, 0];
    const hasMultipleWaypoints = points.length > 4;

    if (hasMultipleWaypoints) {
      // Use source stage as fallback to prevent getting stuck
      target = sourceStageRef === mainStage ? "main" : "offCanvas";
    }
  }

  // Additional fallback for arrows
  if (
    (type === "Arrow" || type === "Line" || type === "DoubleSidedArrow") &&
    target === null
  ) {
    // Keep the element in its original canvas to prevent loss
    target = sourceStageRef === mainStage ? "main" : "offCanvas";
  }

  return target;
};

/**
 * Creates updated element for transfer between canvases
 */
const createTransferElement = (type, draggedElement, newPos) => {
  if (type === "Arrow" || type === "Line" || type === "DoubleSidedArrow") {
    // Arrow/Line points are stored as relative coordinates (relative to group position)
    // When transferring, we need to keep them as relative coordinates for the new position
    const originalPoints = draggedElement.points || [0, 0, 0, 0];
    const hasMultipleWaypoints = originalPoints.length > 4;

    // For arrows with multiple waypoints, ensure all points are properly converted
    let adjustedPoints = originalPoints;
    if (hasMultipleWaypoints) {
      // For arrows with waypoints, we need to be more careful about coordinate conversion
      // The points are already relative to the original group position
      // We need to keep them relative to the new group position
      adjustedPoints = originalPoints.map((coord, i) => {
        // Keep the same relative coordinates - they will be relative to the new group position
        return coord;
      });
    }

    // Points are already relative, so we just need to update the group position
    // The points themselves remain the same (they're relative to the new group position)
    return {
      ...draggedElement,
      x: newPos.x,
      y: newPos.y,
      points: adjustedPoints, // Keep the same relative points
      _isInOffCanvasDuringDrag: undefined, // Clear the drag flag after transfer
    };
  } else {
    // For non-arrow elements (Text, Rect, Circle, Image, etc.), treat newPos as the
    // element's origin in stage coordinates (top-left for Rect/Text, center for Circle,
    // and whatever anchor Image is currently using). This matches how simple elements
    // are handled elsewhere in the drag logic.
    return {
      ...draggedElement,
      x: newPos.x,
      y: newPos.y,
      ...(type === "Text" ? { _lastUpdated: Date.now() } : {}),
      rotation: draggedElement.rotation ?? 0, // Preserve rotation
      // CRITICAL: Preserve isOnTopLayer for images to ensure undo restores correctly
      isOnTopLayer:
        type === "Image"
          ? draggedElement.isOnTopLayer ?? true
          : draggedElement.isOnTopLayer,
      _isInOffCanvasDuringDrag: undefined, // Clear the drag flag after transfer
    };
  }
};

/**
 * Main handler for element drag end - handles transfers between canvases
 */
export const handleElementDragEnd = ({
  id,
  newX,
  newY,
  type,
  sourceStageRef,
  mainState,
  offCanvasState,
  setMainState,
  setOffCanvasState,
  stageRef,
  offCanvasStageRef,
  canvasHeight,
  setSelectedObject,
  setSelectedArrow,
  setSelectedLine,
  setSelectedDoubleSidedArrow,
  setSelectedObjects,
  setSelectedText, // Optional: for text elements
  setSelectedImage, // Optional: for image elements (used by ImageEditor)
  transformerRef,
  offCanvasTransformerRef,
  saveToHistory,
  handleImageDragEnd,
  handleRectangleDragEnd, // Optional: handler for rectangle drag end
  handleCircleDragEnd, // Optional: handler for circle drag end
  handleArrowDragEnd, // Optional: handler for arrow drag end
  handleDoubleSidedArrowDragEnd, // Optional: handler for double-sided arrow drag end
  handleLineDragEnd, // Optional: handler for line drag end
  handleTextDragEnd, // Optional: handler for text drag end
  mouseClientX, // Optional: actual mouse position for accurate coordinate calculation
  mouseClientY,
  currentImage = null, // Optional: current image object to preserve all properties (rotation, scaleX, scaleY, etc.)
  currentRectangle = null, // Optional: current rectangle object to preserve all properties (rotation, scaleX, scaleY, etc.)
  currentCircle = null, // Optional: current circle object to preserve all properties (rotation, scaleX, scaleY, etc.)
  currentArrow = null, // Optional: current arrow object to preserve all properties (rotation, scaleX, scaleY, etc.)
  currentDoubleSidedArrow = null, // Optional: current double-sided arrow object to preserve all properties (rotation, scaleX, scaleY, etc.)
  currentLine = null, // Optional: current line object to preserve all properties (rotation, scaleX, scaleY, etc.)
  currentText = null, // Optional: current text object to preserve all properties (rotation, scaleX, scaleY, etc.)
  setPendingChanges, // Optional: function to mark pending changes for unselection save
  pendingChangesRef, // Optional: ref to update synchronously
  dropPositionInOffCanvasCoords = false, // Optional: when true (Text main→off), newX/newY are already off-canvas stage coords
  dropPositionInMainCoords = false, // Optional: when true (Text off→main), newX/newY are already main stage coords
}) => {
  const clearDragStageFlags = (obj) => {
    if (!obj || typeof obj !== "object") return obj;
    return {
      ...obj,
      _isInOffCanvasDuringDrag: undefined,
      _previewTargetStage: undefined,
      _isDragging: undefined,
    };
  };
  const syncSelectedObjectsForId = (updates = {}) => {
    if (typeof setSelectedObjects !== "function") return;
    setSelectedObjects((prev) =>
      (prev || []).map((obj) =>
        obj?.id === id ? { ...clearDragStageFlags(obj), ...updates } : obj,
      ),
    );
  };

  const mainStage = stageRef.current;
  const offCanvasStage = offCanvasStageRef.current;

  if (!mainStage || !offCanvasStage) {
    console.warn("🔴 [DRAG-END] Missing stage refs", { mainStage: !!mainStage, offCanvasStage: !!offCanvasStage });
    return;
  }

  // Unwrap ref early: callers may pass stageRef.current (stage) or the ref object.
  const sourceStage = (sourceStageRef && typeof sourceStageRef.current !== "undefined")
    ? sourceStageRef.current
    : sourceStageRef;

  const isSourceOffCanvasStage = sourceStage === offCanvasStage;
  const isSourceMainStage = sourceStage === mainStage;
  const elementInMainState = mainState.find((el) => el.id === id);
  const elementInOffCanvasState = offCanvasState.find((el) => el.id === id);

  // Prefer the element from the source stage to avoid wrong ownership when an id
  // temporarily exists in both arrays during sync/transfer edge cases.
  const baseElement = isSourceOffCanvasStage
    ? elementInOffCanvasState || elementInMainState
    : isSourceMainStage
      ? elementInMainState || elementInOffCanvasState
      : elementInMainState || elementInOffCanvasState;

  if (!baseElement) {
    console.warn("🔴 [DRAG-END] Element not found", { id, mainStateCount: mainState?.length, offCanvasStateCount: offCanvasState?.length });
    return;
  }

  // Merge with the strictly up-to-date 'current' version so actual visually-computed
  // width/height and rotation from nodes intercept the logic for calculations.
  const draggedElement = {
    ...baseElement,
    ...(currentText || currentRectangle || currentCircle || currentImage || currentArrow || currentDoubleSidedArrow || currentLine || {})
  };
  const sanitizedDraggedElement = clearDragStageFlags(draggedElement);
  syncSelectedObjectsForId();

  // Resolve origin canvas with source-stage priority. This prevents off-canvas drags
  // from being routed through main-canvas clamp rules when arrays are briefly duplicated.
  const isFromMainCanvas = isSourceOffCanvasStage
    ? false
    : isSourceMainStage
      ? true
      : Boolean(elementInMainState && !elementInOffCanvasState);

  const mainBBox = mainStage.container().getBoundingClientRect();
  const offBBox = offCanvasStage.container().getBoundingClientRect();

  const sourceStageForBBox = sourceStage || mainStage;
  const sourceBBox = sourceStageForBBox.container().getBoundingClientRect();

  // Main canvas boundaries from measured stage container (matches fixed 720×836 stage)
  const mainCanvasRight = mainBBox.left + mainBBox.width;
  const mainCanvasBottom = mainBBox.top + mainBBox.height;

  // Calculate client coordinates for boundary detection
  const calculated = calculateClientCoordinates({
    type,
    draggedElement,
    newX,
    newY,
    sourceBBox,
  });
  let clientX = calculated.clientX;
  let clientY = calculated.clientY;

  // For elements from off-canvas, if mouse position is available and mouse is below main canvas,
  // use mouse position for accurate boundary detection (prevents incorrect conversion to main canvas)
  // This is critical: when dragging below the main canvas, Konva's constrained position might
  // make it appear the element is still in main canvas, causing incorrect conversion
  if (
    !isFromMainCanvas &&
    mouseClientX !== undefined &&
    mouseClientY !== undefined
  ) {
    // If mouse is below main canvas, use mouse position for boundary detection
    // This ensures elements dragged below main canvas stay in off-canvas
    if (mouseClientY > mainCanvasBottom) {
      clientX = mouseClientX;
      clientY = mouseClientY;
    }
  }

  // CRITICAL FIX: For top-layer images from main canvas dragging to off-canvas,
  // use mouse position directly for boundary detection
  // Top-layer images have their node on main canvas stage, but when dragged to off-canvas,
  // the newX/newY might be in off-canvas coordinates, causing incorrect client coordinate calculation
  if (
    type === "Image" &&
    isFromMainCanvas &&
    draggedElement?.isOnTopLayer === true &&
    mouseClientX !== undefined &&
    mouseClientY !== undefined
  ) {
    // Check if mouse is over off-canvas area
    const isMouseOverOffCanvas =
      mouseClientX >= offBBox.left &&
      mouseClientX <= offBBox.right &&
      mouseClientY >= offBBox.top &&
      mouseClientY <= offBBox.bottom;

    if (isMouseOverOffCanvas) {
      // Use mouse position directly for accurate boundary detection
      clientX = mouseClientX;
      clientY = mouseClientY;
    }
  }

  // CRITICAL FIX: For Line/Arrow/DoubleSidedArrow, use mouse position for boundary detection when available.
  // The node stays on the source stage during drag, so newX/newY are in source coords - without mouse position
  // we'd never detect drop over the other stage and the element would not transfer (appearing to vanish).
  if (
    (type === "Line" || type === "Arrow" || type === "DoubleSidedArrow") &&
    mouseClientX !== undefined &&
    mouseClientY !== undefined
  ) {
    const isMouseOverOffCanvas =
      mouseClientX >= offBBox.left &&
      mouseClientX <= offBBox.right &&
      mouseClientY >= offBBox.top &&
      mouseClientY <= offBBox.bottom;
    const isMouseOverMain =
      mouseClientX >= mainBBox.left &&
      mouseClientX <= mainBBox.right &&
      mouseClientY >= mainBBox.top &&
      mouseClientY <= mainBBox.bottom;
    if (isMouseOverOffCanvas || isMouseOverMain) {
      clientX = mouseClientX;
      clientY = mouseClientY;
    }
  }

  // CRITICAL FIX: For Text, use mouse position for boundary detection when available.
  // Off-canvas → main: calculated client coords use sourceBBox (off-canvas), so they always
  // point to off-canvas and we'd never get target === "main" when the user drops on main.
  // Main → off-canvas (HTML drag path): the Konva group stays on the main stage, so newX/newY
  // never reach off-canvas; without mouse position we'd never detect target === "offCanvas".
  let boundaryClientX = clientX;
  let boundaryClientY = clientY;

  if (type === "Text" && mouseClientX !== undefined && mouseClientY !== undefined) {
    const isMouseOverMain =
      mouseClientX >= mainBBox.left &&
      mouseClientX <= mainBBox.right &&
      mouseClientY >= mainBBox.top &&
      mouseClientY <= mainBBox.bottom;
    const isMouseOverOffCanvas =
      mouseClientX >= offBBox.left &&
      mouseClientX <= offBBox.right &&
      mouseClientY >= offBBox.top &&
      mouseClientY <= offBBox.bottom;

    // Only override when pointer is actually over one of the canvases; this keeps
    // same‑canvas drags that end inside the canvas behaving as before.
    if (isMouseOverMain || isMouseOverOffCanvas) {
      boundaryClientX = mouseClientX;
      boundaryClientY = mouseClientY;
    }
  }

  // Determine target canvas (pass unwrapped stage so ref vs stage comparison works)
  const target = determineTargetCanvas({
    type,
    draggedElement,
    clientX: boundaryClientX,
    clientY: boundaryClientY,
    mainBBox,
    offBBox,
    mainCanvasRight,
    mainCanvasBottom,
    sourceStageRef: sourceStageForBBox,
    mainStage,
    offCanvasStage,
    isFromMainCanvas, // Pass whether element originally belonged to main canvas
  });
  // Compare stages - handle both React Konva Stage components and direct Konva Stage objects
  // React Konva Stage components have a getStage() method to get the underlying Konva stage
  const getStageObject = (stageRef) => {
    if (!stageRef) return null;
    // If it's a React Konva Stage component, get the underlying Konva stage
    if (typeof stageRef.getStage === 'function') {
      return stageRef.getStage();
    }
    // If it's already a Konva stage, return it
    return stageRef;
  };

  const sourceStageObj = getStageObject(sourceStageForBBox);
  const mainStageObj = getStageObject(mainStage);
  const offCanvasStageObj = getStageObject(offCanvasStage);

  // Compare by both reference equality and by getting the underlying stage objects
  // CRITICAL: Use isFromMainCanvas (from state check) as the primary indicator, not stage comparison
  // This is more reliable because it checks where the element actually is in state
  const isFromMain = isFromMainCanvas && (sourceStageForBBox === mainStage || sourceStageObj === mainStageObj ||
    (sourceStageObj && mainStageObj && sourceStageObj.id() === mainStageObj.id()));
  const isFromOffCanvas = !isFromMainCanvas && (sourceStageForBBox === offCanvasStage || sourceStageObj === offCanvasStageObj ||
    (sourceStageObj && offCanvasStageObj && sourceStageObj.id() === offCanvasStageObj.id()));

  const shouldTransferToOffCanvas = isFromMain && target === "offCanvas";
  const shouldTransferToMain = isFromOffCanvas && target === "main";

  // Compute comparison results for logging
  const sourceStageRefEqualsMain = sourceStageForBBox === mainStage;
  const sourceStageObjEqualsMain = sourceStageObj === mainStageObj;
  const targetIsOffCanvas = target === "offCanvas";
  const targetIsMain = target === "main";


  if (shouldTransferToOffCanvas || (isFromMain && target === "offCanvas")) {
    // Transfer from main to off-canvas
    // Use mouse position if available for accurate positioning, otherwise use calculated client coordinates
    let finalClientX = clientX;
    let finalClientY = clientY;

    if (mouseClientX !== undefined && mouseClientY !== undefined) {
      // Use actual pointer drop location when transferring between stages.
      // Source-stage newX/newY can be clamped near stage edges (often 0), which causes
      // transferred elements to snap to incorrect positions in off-canvas.
      finalClientX = mouseClientX;
      finalClientY = mouseClientY;
    }

    // For arrows/lines, clientX/clientY represents the center, but we need the group origin
    // Calculate the group origin position such that the center ends up at finalClientX/finalClientY
    let newPos;
    if (type === "Text" && dropPositionInOffCanvasCoords) {
      // Text main→off: caller passed preview position already in off-canvas stage coords (ghost followed cursor)
      newPos = { x: Math.round(newX), y: Math.round(newY) };
    } else if (type === "Arrow" || type === "Line" || type === "DoubleSidedArrow") {
      // Calculate bounding box of points relative to group origin
      const points = draggedElement.points || [0, 0, 0, 0];
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (let i = 0; i < points.length; i += 2) {
        minX = Math.min(minX, points[i]);
        maxX = Math.max(maxX, points[i]);
        minY = Math.min(minY, points[i + 1]);
        maxY = Math.max(maxY, points[i + 1]);
      }
      const centerOffsetX = (minX + maxX) / 2;
      const centerOffsetY = (minY + maxY) / 2;

      // Convert final center position to stage coordinates
      const centerStageX = finalClientX - offBBox.left;
      const centerStageY = finalClientY - offBBox.top;

      // Calculate group origin: center position minus center offset
      newPos = {
        x: centerStageX - centerOffsetX,
        y: centerStageY - centerOffsetY,
      };
    } else {
      // For simple elements, finalClientX/finalClientY is already in client space.
      // Text still uses center-based coordinates (calculateClientCoordinates),
      // while Rect uses top-left (we changed Rect to match its state model).
      if (type === "Text") {
        // Text (when not dropPositionInOffCanvasCoords): treat finalClientX/finalClientY as center, convert to top-left
        const elemWidth = draggedElement.width || 0;
        const elemHeight = draggedElement.height || 0;
        newPos = {
          x: Math.round(finalClientX - offBBox.left - elemWidth / 2),
          y: Math.round(finalClientY - offBBox.top - elemHeight / 2),
        };
      } else if (type === "Rect") {
        // Rect: finalClientX/finalClientY already represents the top-left in client space;
        // map directly into off-canvas stage coordinates without an extra half-width/height shift.
        newPos = {
          x: Math.round(finalClientX - offBBox.left),
          y: Math.round(finalClientY - offBBox.top),
        };
      } else {
        // Other simple elements (Image, Circle): treat finalClientX/finalY as origin
        newPos = {
          x: Math.round(finalClientX - offBBox.left),
          y: Math.round(finalClientY - offBBox.top),
        };
      }
    }

    // Constrain transferred element to off-canvas bounds/strip using DROP side
    // (not source side), so the position stays consistent with where it was dropped.
    const dropExtent = getAbsoluteHorizontalExtent(draggedElement, type, newPos.x);
    const dropSide = getOffCanvasHorizontalStripSide(
      dropExtent.minX,
      dropExtent.maxX,
      offCanvasStage.width() || window.innerWidth,
    );
    const offCanvasConstrained = constrainOffCanvasPosition(
      draggedElement,
      type,
      newPos.x,
      newPos.y,
      offCanvasStage,
      dropSide,
      getTransformerEdgeInsetForType(type),
    );
    newPos = {
      x: Math.round(offCanvasConstrained.constrainedX),
      y: Math.round(offCanvasConstrained.constrainedY),
    };

    // CRITICAL: Save the state BEFORE the transfer so undo can restore it
    // This ensures that when you undo, the image goes back to main canvas
    // We need to explicitly pass the current state (before transfer) to saveToHistory
    // REMOVED: We no longer save before_drag_transfer here. Instead, we track that the element
    // has moved canvases, and on unselection, we save the final state with the element in the new array.
    // On undo, we detect the canvas change and move the element back.

    const updatedElement = createTransferElement(type, sanitizedDraggedElement, newPos);

    // Compute updated state arrays directly to ensure correct history
    const updatedMainState = mainState.filter((el) => el.id !== id);
    const updatedOffCanvasState = [...offCanvasState, updatedElement];

    // Add to off-canvas state
    setOffCanvasState((prev) => {
      const updated = [...prev, updatedElement];
      const hasElement = updated.some((el) => el.id === id);
      if (!hasElement) {
        console.error("🔴 [DRAG-TRANSFER] ERROR: Element not found in off-canvas state after addition!", { id });
      }
      return updated;
    });

    // Remove from main state
    setMainState((prev) => {
      const filtered = prev.filter((el) => el.id !== id);
      const stillInMain = filtered.some((el) => el.id === id);
      if (stillInMain) {
        console.error("🔴 [DRAG-TRANSFER] ERROR: Element still in main state after removal!", { id });
      }
      return filtered;
    });

    // Update selectedObject to the transferred element so it remains visible
    // This ensures the element is visible immediately after transfer (like rectangles)
    // _canvas flag lets useTransformerManager detect the canvas change and re-attach the transformer
    setSelectedObject({
      ...updatedElement,
      type,
      _canvas: "offCanvas",
      _previewTargetStage: undefined,
      _isInOffCanvasDuringDrag: undefined,
    });
    syncSelectedObjectsForId({
      x: updatedElement.x,
      y: updatedElement.y,
      _canvas: "offCanvas",
      type,
    });

    // Clear other selections
    setSelectedArrow(null);
    setSelectedLine(null);
    setSelectedDoubleSidedArrow(null);

    // For text elements, also update selectedText to keep it visible
    if (type === "Text" && setSelectedText) {
      setSelectedText({
        ...updatedElement,
        type: "Text",
        _previewTargetStage: undefined,
        _isInOffCanvasDuringDrag: undefined,
      });
    }

    // Clear transformers from both stages
    clearTransformers(transformerRef, offCanvasTransformerRef);

    // Mark pending changes so that when the element is unselected, it will save to history
    if (typeof setPendingChanges === "function") {
      setPendingChanges(true);
      if (pendingChangesRef) {
        pendingChangesRef.current = true; // Update ref synchronously
      }
    }

    // REMOVED: We no longer save drag_transfer here. Instead, the element remains selected
    // after the transfer, and when it's unselected, we save the final state with the element
    // in the new array. On undo, we detect the canvas change and move the element back.

    // CRITICAL: Return early after transfer to prevent any further processing
    // This ensures handleTextDragEnd is not called for transferred elements
    return;
  } else if (shouldTransferToMain || (isFromOffCanvas && target === "main")) {
    // Transfer from off-canvas to main
    // Use mouse position if available for accurate positioning, otherwise use calculated client coordinates
    let finalClientX = clientX;
    let finalClientY = clientY;

    if (mouseClientX !== undefined && mouseClientY !== undefined) {
      // Line/Arrow: node stays on source (off-canvas) stage; drop position = mouse. Place center at mouse.
      if (type === "Arrow" || type === "Line" || type === "DoubleSidedArrow") {
        finalClientX = mouseClientX;
        finalClientY = mouseClientY;
      } else if (isFromOffCanvas && type !== "Rect" && type !== "Image" && type !== "Circle") {
        // Off-canvas → main: for simple elements (Text),
        // drop directly under the mouse. For Rect, Image, and Circle we rely on the
        // coordinate mapping from calculateClientCoordinates / source coords,
        // so we DON'T override with mouse position here.
        finalClientX = mouseClientX;
        finalClientY = mouseClientY;
      } else {
        // Main → main (same stage) or main → off: preserve drag offset so element stays under cursor.
        const sourceStage = sourceStageForBBox;
        const stageX = sourceStage?.x() || 0;
        const stageY = sourceStage?.y() || 0;
        const stageScaleX = sourceStage?.scaleX() || 1;
        const stageScaleY = sourceStage?.scaleY() || 1;
        const elementOriginStageX = newX;
        const elementOriginStageY = newY;
        const elementOriginClientX =
          sourceBBox.left + elementOriginStageX * stageScaleX + stageX;
        const elementOriginClientY =
          sourceBBox.top + elementOriginStageY * stageScaleY + stageY;
        const dragOffsetX = mouseClientX - elementOriginClientX;
        const dragOffsetY = mouseClientY - elementOriginClientY;
        finalClientX = mouseClientX - dragOffsetX;
        finalClientY = mouseClientY - dragOffsetY;
      }
    }

    // For arrows/lines, clientX/clientY represents the center, but we need the group origin
    // Calculate the group origin position such that the center ends up at finalClientX/finalClientY
    let newPos;
    if (type === "Text" && dropPositionInMainCoords) {
      // Text off→main: caller passed preview position already in main stage coords (ghost followed cursor)
      newPos = { x: Math.round(newX), y: Math.round(newY) };
    } else if (type === "Arrow" || type === "Line" || type === "DoubleSidedArrow") {
      // Calculate bounding box of points relative to group origin
      const points = draggedElement.points || [0, 0, 0, 0];
      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;
      for (let i = 0; i < points.length; i += 2) {
        minX = Math.min(minX, points[i]);
        maxX = Math.max(maxX, points[i]);
        minY = Math.min(minY, points[i + 1]);
        maxY = Math.max(maxY, points[i + 1]);
      }
      const centerOffsetX = (minX + maxX) / 2;
      const centerOffsetY = (minY + maxY) / 2;

      // Convert final center position to stage coordinates
      const centerStageX = finalClientX - mainBBox.left;
      const centerStageY = finalClientY - mainBBox.top;

      // Calculate group origin: center position minus center offset
      newPos = {
        x: centerStageX - centerOffsetX,
        y: centerStageY - centerOffsetY,
      };
    } else {
      // For simple elements (Text, Rect, Circle, Image), map the element's position from
      // client space to main stage coordinates. At this point, finalClientX/finalClientY
      // represent the point we want to use as the element's origin on the main canvas
      // (for off-canvas elements we already snapped them under the mouse above).
      const mainScaleX = mainStage?.scaleX?.() ?? 1;
      const mainScaleY = mainStage?.scaleY?.() ?? 1;
      const mainStageX = mainStage?.x?.() ?? 0;
      const mainStageY = mainStage?.y?.() ?? 0;

      const stageX = (finalClientX - mainBBox.left) / mainScaleX - mainStageX;
      const stageY = (finalClientY - mainBBox.top) / mainScaleY - mainStageY;
      newPos = {
        x: Math.round(stageX),
        y: Math.round(stageY),
      };
    }

    const updatedElement = createTransferElement(type, sanitizedDraggedElement, newPos);

    // REMOVED: We no longer save before_drag_transfer here. Instead, we track that the element
    // has moved canvases, and on unselection, we save the final state with the element in the new array.
    // On undo, we detect the canvas change and move the element back.

    // Compute updated state arrays directly to ensure correct history
    const updatedMainState = [...mainState, updatedElement];
    const updatedOffCanvasState = offCanvasState.filter((el) => el.id !== id);

    // Add to main state
    setMainState((prev) => [...prev, updatedElement]);

    // Remove from off-canvas state
    setOffCanvasState((prev) => prev.filter((el) => el.id !== id));

    // Update selectedObject to the transferred element so it remains visible
    // This ensures the element is visible immediately after transfer (like rectangles)
    // _canvas flag lets useTransformerManager detect the canvas change and re-attach the transformer
    setSelectedObject({
      ...updatedElement,
      type,
      _canvas: "main",
      _previewTargetStage: undefined,
      _isInOffCanvasDuringDrag: undefined,
    });
    syncSelectedObjectsForId({
      x: updatedElement.x,
      y: updatedElement.y,
      _canvas: "main",
      type,
    });

    // Clear other selections
    setSelectedArrow(null);
    setSelectedLine(null);
    setSelectedDoubleSidedArrow(null);

    // For text elements, also update selectedText to keep it visible
    if (type === "Text" && setSelectedText) {
      setSelectedText({
        ...updatedElement,
        type: "Text",
        _previewTargetStage: undefined,
        _isInOffCanvasDuringDrag: undefined,
      });
    }

    // Clear transformers from both stages
    clearTransformers(transformerRef, offCanvasTransformerRef);

    // Mark pending changes so that when the element is unselected, it will save to history
    if (typeof setPendingChanges === "function") {
      setPendingChanges(true);
      if (pendingChangesRef) {
        pendingChangesRef.current = true; // Update ref synchronously
      }
    }

    // REMOVED: We no longer save drag_transfer here. Instead, the element remains selected
    // after the transfer, and when it's unselected, we save the final state with the element
    // in the new array. On undo, we detect the canvas change and move the element back.

    // CRITICAL: Return early after transfer to prevent any further processing
    // This ensures handleTextDragEnd is not called for transferred elements
    return;
  } else {
    // Element stays in the same canvas, just update its position
    // CRITICAL FIX: Round coordinates immediately to prevent sub-pixel positioning errors
    let finalX = Math.round(newX);
    let finalY = Math.round(newY);

    // For off-canvas elements dragged below main canvas, ensure coordinates are correct
    // When dragging below the visible area, Konva might constrain coordinates to the visible area
    // Use mouse position to calculate the correct position directly
    // CRITICAL FIX: Skip this for Text elements - they handle positioning correctly via Konva
    // Text elements have complex internal structure (Html components) and using mouse position
    // causes them to "jump" to center on the cursor instead of staying where dragged
    if (
      !isFromMainCanvas &&
      target === "offCanvas" &&
      sourceStageForBBox === offCanvasStage &&
      type !== "Text" && // Text already has dedicated coordinate handling
      type !== "Rect" &&
      type !== "Image" &&
      type !== "Circle" &&
      type !== "Line" &&
      type !== "Arrow" &&
      type !== "DoubleSidedArrow"
    ) {
      const isBelowMainCanvas = clientY > mainCanvasBottom;

      if (
        isBelowMainCanvas &&
        mouseClientX !== undefined &&
        mouseClientY !== undefined
      ) {
        // When dragged below main canvas, Konva's newX/newY is constrained to the visible area
        // The mouse position tells us where the user dragged to
        // We need to calculate the element's position based on the mouse position

        // Calculate where the element was BEFORE this drag (original position) in client coordinates
        const originalElementClientX = offBBox.left + draggedElement.x;
        const originalElementClientY = offBBox.top + draggedElement.y;

        // Calculate where Konva positioned the element (constrained position) in client coordinates
        const constrainedElementClientX = sourceBBox.left + newX;
        const constrainedElementClientY = sourceBBox.top + newY;

        // The key insight: Konva maintains the drag offset during the drag
        // So if the mouse moved by delta, the element should also move by delta
        // But we need to calculate the delta from where the mouse started, not from the element origin

        // We can estimate where the mouse started by using the constrained position
        // The constrained position tells us where Konva put the element, and the mouse tells us where it should be
        // The difference between mouse and constrained position is approximately the drag offset
        // But we need the mouse's starting position, not the element's starting position

        // Actually, simpler: the mouse moved from some start position to mouseClientX/Y
        // The element should move by the same amount
        // We can estimate the mouse start position from the constrained element position + drag offset
        // But we don't know the drag offset...

        // Let's try: use the mouse position directly, accounting for a small estimated drag offset
        // The drag offset is the difference between mouse and constrained element position
        const estimatedDragOffsetX = mouseClientX - constrainedElementClientX;
        const estimatedDragOffsetY = mouseClientY - constrainedElementClientY;

        // The element's origin should be at: mouse position - drag offset (in stage coordinates)
        const mouseStageX = mouseClientX - offBBox.left;
        const mouseStageY = mouseClientY - offBBox.top;

        // But wait, this gives us the constrained position again!
        // The real issue: we need to know where the mouse STARTED, not where it is now

        // Alternative: assume the drag offset is small and just use mouse position
        // Or: calculate from the difference between mouse and original element position
        // If mouse moved from originalElementClientX to mouseClientX, element should move by same amount
        const mouseDeltaClientX = mouseClientX - originalElementClientX;
        const mouseDeltaClientY = mouseClientY - originalElementClientY;

        // Convert to stage coordinates and apply to original position
        finalX = draggedElement.x + mouseDeltaClientX;
        finalY = draggedElement.y + mouseDeltaClientY;
      }
    }

    // Normalize same-canvas drag-end position to that stage's valid bounds.
    // This prevents occasional negative/newX spikes (e.g. -211) from leaking into state.
    if (target === "main" && mainStage) {
      const stageWidth = mainStage.width() || window.innerWidth * 0.45;
      const stageHeight =
        mainStage.height() || getStageHeightPxFromCanvasHeight(canvasHeight);
      const edgeInset = getTransformerEdgeInsetForType(type);

      // Lines/arrows use group-origin coordinates; clamp by actual point extents,
      // not by generic width/height from a top-left bbox model.
      if (
        (type === "Line" || type === "Arrow" || type === "DoubleSidedArrow") &&
        draggedElement?.points?.length >= 4
      ) {
        const points = draggedElement.points;
        let minPointX = Infinity;
        let minPointY = Infinity;
        let maxPointX = -Infinity;
        let maxPointY = -Infinity;
        for (let i = 0; i < points.length; i += 2) {
          minPointX = Math.min(minPointX, points[i]);
          maxPointX = Math.max(maxPointX, points[i]);
          minPointY = Math.min(minPointY, points[i + 1]);
          maxPointY = Math.max(maxPointY, points[i + 1]);
        }

        const strokeWidth = draggedElement.strokeWidth || 2;
        const padding = Math.max(strokeWidth / 2, 5);
        const minAllowedAbsX = edgeInset + padding;
        const maxAllowedAbsX = stageWidth - edgeInset - padding;
        const minAllowedAbsY = edgeInset + padding;
        const maxAllowedAbsY = stageHeight - edgeInset - padding;

        const elementAbsWidth = maxPointX - minPointX;
        const elementAbsHeight = maxPointY - minPointY;
        const allowedWidth = Math.max(0, maxAllowedAbsX - minAllowedAbsX);
        const allowedHeight = Math.max(0, maxAllowedAbsY - minAllowedAbsY);

        if (elementAbsWidth >= allowedWidth) {
          finalX = minAllowedAbsX - minPointX;
        } else {
          const absMinX = minPointX + finalX;
          const absMaxX = maxPointX + finalX;
          if (absMinX < minAllowedAbsX) {
            finalX += minAllowedAbsX - absMinX;
          } else if (absMaxX > maxAllowedAbsX) {
            finalX -= absMaxX - maxAllowedAbsX;
          }
        }

        if (elementAbsHeight >= allowedHeight) {
          finalY = minAllowedAbsY - minPointY;
        } else {
          const absMinY = minPointY + finalY;
          const absMaxY = maxPointY + finalY;
          if (absMinY < minAllowedAbsY) {
            finalY += minAllowedAbsY - absMinY;
          } else if (absMaxY > maxAllowedAbsY) {
            finalY -= absMaxY - maxAllowedAbsY;
          }
        }

        finalX = Math.round(finalX);
        finalY = Math.round(finalY);
      }

      // Rectangles/images/text can overflow with simple bbox clamp at x=0/y=0,
      // especially when runtime-rendered bounds differ from model width/height.
      // Use rendered clientRect when possible (same behavior as rectangles).
      if (
        type === "Rect" ||
        type === "Image" ||
        type === "Text" ||
        type === "Circle"
      ) {
        // Top-layer elements can live on a dedicated stage, so prefer the stage
        // that actually owns the dragged node when available.
        const mainLookupStage =
          sourceStage &&
          sourceStage !== offCanvasStage &&
          typeof sourceStage.findOne === "function" &&
          sourceStage.findOne(`#${id}`)
            ? sourceStage
            : mainStage;
        const elementNode =
          typeof mainLookupStage?.findOne === "function"
            ? mainLookupStage.findOne(`#${id}`)
            : null;
        if (elementNode) {
          const clamped = clampWithRenderedClientRect({
            stage: mainLookupStage,
            node: elementNode,
            candidateX: finalX,
            candidateY: finalY,
            edgeInsetPx: getTransformerEdgeInsetForType(type),
            horizontalSlackPx:
              type === "Text" && isEffectivelyEmptyText(draggedElement?.text)
                ? EMPTY_TEXT_HORIZONTAL_CLAMP_SLACK_PX
                : 0,
            topSlackPx:
              type === "Text" && isEffectivelyEmptyText(draggedElement?.text)
                ? EMPTY_TEXT_TOP_CLAMP_SLACK_PX
                : 0,
            bottomSlackPx:
              type === "Text" && isEffectivelyEmptyText(draggedElement?.text)
                ? EMPTY_TEXT_BOTTOM_CLAMP_SLACK_PX
                : 0,
            offCanvasMainColumnSlackPx:
              type === "Text" && isEffectivelyEmptyText(draggedElement?.text)
                ? EMPTY_TEXT_OFFCANVAS_MAIN_COLUMN_SLACK_PX
                : 0,
            roundResult: type !== "Image",
          });
          finalX = clamped.x;
          finalY = clamped.y;
        } else {
          const bbox = getSimpleBoundingBox(draggedElement, type);
          const inset = getTransformerEdgeInsetForType(type);
          const insetX = getEffectiveInset(inset, stageWidth, bbox.width);
          const insetY = getEffectiveInset(inset, stageHeight, bbox.height);
          finalX = Math.round(
            Math.max(insetX, Math.min(stageWidth - bbox.width - insetX, finalX)),
          );
          finalY = Math.round(
            Math.max(insetY, Math.min(stageHeight - bbox.height - insetY, finalY)),
          );
        }
      } else if (type !== "Line" && type !== "Arrow" && type !== "DoubleSidedArrow") {
        const bbox = getSimpleBoundingBox(draggedElement, type);
        if (bbox.isCenterCoordinate) {
          let halfW = bbox.width / 2;
          let halfH = bbox.height / 2;
          // Rotation-aware fallback for circles/ellipses when node lookup fails.
          // Without this, slight overflow can remain at drag-end for rotated ellipses.
          if (type === "Circle") {
            const fallbackRadius =
              draggedElement?.radius ||
              Math.max(draggedElement?.radiusX || 0, draggedElement?.radiusY || 0) ||
              0;
            const rx =
              draggedElement?.radiusX !== undefined &&
              draggedElement?.radiusX !== null
                ? Number(draggedElement.radiusX) || 0
                : Number(fallbackRadius) || 0;
            const ry =
              draggedElement?.radiusY !== undefined &&
              draggedElement?.radiusY !== null
                ? Number(draggedElement.radiusY) || 0
                : Number(fallbackRadius) || 0;
            const strokeHalf = Math.max(
              0,
              Number(draggedElement?.strokeWidth || 0) / 2,
            );
            const rotationDeg = Number(draggedElement?.rotation || 0);
            const rotationRad = (rotationDeg * Math.PI) / 180;
            const absCos = Math.abs(Math.cos(rotationRad));
            const absSin = Math.abs(Math.sin(rotationRad));
            halfW = Math.sqrt((rx * absCos) ** 2 + (ry * absSin) ** 2) + strokeHalf;
            halfH = Math.sqrt((rx * absSin) ** 2 + (ry * absCos) ** 2) + strokeHalf;
          }
          const insetX = getEffectiveInset(edgeInset, stageWidth, halfW * 2);
          const insetY = getEffectiveInset(edgeInset, stageHeight, halfH * 2);
          finalX = Math.round(
            Math.max(halfW + insetX, Math.min(stageWidth - halfW - insetX, finalX)),
          );
          finalY = Math.round(
            Math.max(halfH + insetY, Math.min(stageHeight - halfH - insetY, finalY)),
          );
        } else {
          const insetX = getEffectiveInset(edgeInset, stageWidth, bbox.width);
          const insetY = getEffectiveInset(edgeInset, stageHeight, bbox.height);
          finalX = Math.round(
            Math.max(insetX, Math.min(stageWidth - bbox.width - insetX, finalX)),
          );
          finalY = Math.round(
            Math.max(insetY, Math.min(stageHeight - bbox.height - insetY, finalY)),
          );
        }
      }
    } else if (target === "offCanvas" && offCanvasStage) {
      // Text/Rect: skip generic bbox pre-clamp and rely on rendered client-rect clamp below.
      // Generic bbox can be stale at drag end for rotated shapes and can snap X back unexpectedly.
      if (type === "Text" || type === "Rect") {
        finalX = Math.round(finalX);
        finalY = Math.round(finalY);
      } else {
        const bounded = constrainOffCanvasPosition(
          draggedElement,
          type,
          finalX,
          finalY,
          offCanvasStage,
          null,
          getTransformerEdgeInsetForType(type),
        );
        finalX = Math.round(bounded.constrainedX);
        finalY = Math.round(bounded.constrainedY);
      }

      // Rotation-aware off-canvas clamp for rectangles/images/text so rendered bounds
      // stay in stage and never overlap the off-canvas main-column zone.
      if (
        type === "Rect" ||
        type === "Image" ||
        type === "Text" ||
        type === "Circle"
      ) {
        // Off-canvas top-layer elements may be on a dedicated top-layer stage.
        const offCanvasLookupStage =
          sourceStage &&
          sourceStage !== mainStage &&
          typeof sourceStage.findOne === "function" &&
          sourceStage.findOne(`#${id}`)
            ? sourceStage
            : offCanvasStage;
        const elementNode =
          typeof offCanvasLookupStage?.findOne === "function"
            ? offCanvasLookupStage.findOne(`#${id}`)
            : null;
        if (elementNode) {
          const clamped = clampWithRenderedClientRect({
            stage: offCanvasLookupStage,
            node: elementNode,
            candidateX: finalX,
            candidateY: finalY,
            avoidOffCanvasMainColumn: true,
            edgeInsetPx: getTransformerEdgeInsetForType(type),
            horizontalSlackPx:
              type === "Text" && isEffectivelyEmptyText(draggedElement?.text)
                ? EMPTY_TEXT_HORIZONTAL_CLAMP_SLACK_PX
                : 0,
            topSlackPx:
              type === "Text" && isEffectivelyEmptyText(draggedElement?.text)
                ? EMPTY_TEXT_TOP_CLAMP_SLACK_PX
                : 0,
            bottomSlackPx:
              type === "Text" && isEffectivelyEmptyText(draggedElement?.text)
                ? EMPTY_TEXT_BOTTOM_CLAMP_SLACK_PX
                : 0,
            offCanvasMainColumnSlackPx:
              type === "Text" && isEffectivelyEmptyText(draggedElement?.text)
                ? EMPTY_TEXT_OFFCANVAS_MAIN_COLUMN_SLACK_PX
                : 0,
            roundResult: type !== "Image",
          });
          finalX = clamped.x;
          finalY = clamped.y;
        }
      }
    }
    // CRITICAL: For element types with dedicated handlers (Image, Rect, Circle, Arrow, DoubleSidedArrow, Line, Text),
    // DON'T update state here - let the handler do it to ensure all properties (rotation, scaleX, scaleY) are preserved
    // Only update state directly for element types without dedicated handlers
    const hasDedicatedHandler =
      type === "Image" ||
      type === "Rect" ||
      type === "Circle" ||
      type === "Arrow" ||
      type === "DoubleSidedArrow" ||
      type === "Line" ||
      type === "Text";

    if (!hasDedicatedHandler) {
      const updateState = (prev) =>
        prev.map((el) =>
          el.id === id
            ? {
              ...el,
              x: finalX,
              y: finalY,
              rotation: el.rotation ?? 0, // Preserve rotation
            }
            : el
        );

      if (target === "main") {
        setMainState(updateState);
      } else {
        setOffCanvasState(updateState);
      }

      // CRITICAL: Don't update selectedObject here for images - let handleImageDragEnd update it
      // For images, we'll update selectedObject after handleImageDragEnd with the correct position
      if (type !== "Image") {
        // Update selectedObject with the final converted coordinates (for non-image elements)
        const finalCoords = {
          x: finalX,
          y: finalY,
          _isInOffCanvasDuringDrag: undefined,
          _previewTargetStage: undefined,
        };
        setSelectedObject({ ...draggedElement, ...finalCoords, type });
        syncSelectedObjectsForId({ ...finalCoords, type });
      }
    } else {
      // For elements with dedicated handlers, update selectedObject but let handler update state
      // CRITICAL: Don't update selectedObject here for images - let handleImageDragEnd update it
      if (type !== "Image") {
        const finalCoords = {
          x: finalX,
          y: finalY,
          _isInOffCanvasDuringDrag: undefined,
          _previewTargetStage: undefined,
        };
        setSelectedObject({ ...draggedElement, ...finalCoords, type });
        syncSelectedObjectsForId({ ...finalCoords, type });
      }
    }

    // Save to history or delegate to element-specific handler
    if (type === "Image") {
      try {
        // CRITICAL: Use currentImage if provided (from renderer), otherwise find from state
        // currentImage should have the latest properties including rotation, scaleX, scaleY, etc.
        const dragged = clearDragStageFlags(
          currentImage ||
            (Array.isArray(mainState) ? mainState : []).find(
              (el) => el.id === id
            ) ||
            (Array.isArray(offCanvasState) ? offCanvasState : []).find(
              (el) => el.id === id
            ),
        );

        // CRITICAL FIX: Constrain positions if image is staying in off-canvas
        // CRITICAL: Off-canvas top-layer images also need constraints applied
        let constrainedX = finalX;
        let constrainedY = finalY;
        const isImageOffCanvas = !mainState.some((el) => el.id === id) && offCanvasState.some((el) => el.id === id);
        const isStayingInOffCanvas = isImageOffCanvas && !shouldTransferToMain;
        // CRITICAL FIX: Also constrain off-canvas top-layer images that are staying in off-canvas
        const isOffCanvasTopLayerStaying = isImageOffCanvas && !shouldTransferToMain && dragged?.isOnTopLayer === true;

        if (isStayingInOffCanvas && offCanvasStage && dragged) {
          const { constrainedX: constrainedXResult, constrainedY: constrainedYResult } = constrainOffCanvasPosition(
            dragged,
            "Image",
            finalX,
            finalY,
            offCanvasStage,
            null,
            getTransformerEdgeInsetForType("Image"),
          );
          constrainedX = constrainedXResult;
          constrainedY = constrainedYResult;

          // Final rotation-aware clamp for off-canvas images.
          const offCanvasImageLookupStage =
            sourceStage &&
            sourceStage !== mainStage &&
            typeof sourceStage.findOne === "function" &&
            sourceStage.findOne(`#${id}`)
              ? sourceStage
              : offCanvasStage;
          if (typeof offCanvasImageLookupStage?.findOne === "function") {
            const imageNode = offCanvasImageLookupStage.findOne(`#${id}`);
            if (imageNode) {
              const clamped = clampWithRenderedClientRect({
                stage: offCanvasImageLookupStage,
                node: imageNode,
                candidateX: constrainedX,
                candidateY: constrainedY,
                avoidOffCanvasMainColumn: true,
                edgeInsetPx: TRANSFORMER_ANCHOR_BUFFER_PX,
                roundResult: false,
              });
              constrainedX = clamped.x;
              constrainedY = clamped.y;
            }
          }

          if (finalX !== constrainedX || finalY !== constrainedY) {
            console.warn(
              "🔧 [DRAG-END] Constraining image position in off-canvas to stay within bounds",
              {
                id,
                originalX: finalX,
                originalY: finalY,
                constrainedX,
                constrainedY,
                isTopLayer: dragged?.isOnTopLayer,
              }
            );
          }
        }

        const finalWidth = dragged?.width ?? 0;
        const finalHeight = dragged?.height ?? 0;

        // CRITICAL FIX: For any image that lives on off-canvas and is staying there, update offCanvasImages.
        // handleImageDragEnd only updates main canvas (setImages), so we must update off-canvas state here
        // for both top-layer and non-top-layer off-canvas images so position is persisted and element_edit_complete saves it.
        const isImageInOffCanvas = !mainState.some((el) => el.id === id) && offCanvasState.some((el) => el.id === id);
        const isOffCanvasTopLayerImage = isImageInOffCanvas && dragged?.isOnTopLayer === true;

        if (isStayingInOffCanvas && isImageInOffCanvas && setOffCanvasState) {
          // Update offCanvasImages with new position (all off-canvas images that stayed on off-canvas)
          setOffCanvasState((prevImages) => {
            const existingImage = dragged || prevImages.find((img) => img.id === id);
            if (!existingImage) {
              console.warn("handleImageDragEnd: off-canvas image not found", id);
              return prevImages;
            }

            const updatedImages = prevImages.map((img) =>
              img.id === id
                ? {
                  ...existingImage,
                  x: constrainedX,
                  y: constrainedY,
                  width: finalWidth,
                  height: finalHeight,
                }
                : img
            );

            // CRITICAL: Mark pending changes so endElementEditing knows there was a change
            // This ensures undo button becomes enabled after dragging an existing element
            if (typeof setPendingChanges === "function") {
              setPendingChanges(true);
              if (pendingChangesRef) {
                pendingChangesRef.current = true;
              }
            }

            // Only save image_drag_end for top-layer (legacy); non-top-layer relies on element_edit_complete on unselect
            if (isOffCanvasTopLayerImage && saveToHistory) {
              setTimeout(() => {
                saveToHistory("image_drag_end", null, null, null, null, null, null, null, null, null, null, null, null, null, null, updatedImages);
              }, 0);
            }

            return updatedImages;
          });

          // Keep Konva node visually synced to final clamped coords immediately.
          if (offCanvasStage && typeof offCanvasStage.findOne === "function") {
            try {
              const offCanvasImageNode = offCanvasStage.findOne(`#${id}`);
              if (offCanvasImageNode && typeof offCanvasImageNode.position === "function") {
                offCanvasImageNode.position({
                  x: Math.round(constrainedX),
                  y: Math.round(constrainedY),
                });
                const offLayer = offCanvasImageNode.getLayer?.();
                if (offLayer && typeof offLayer.batchDraw === "function") {
                  offLayer.batchDraw();
                }
              }
            } catch (_) {
              // no-op
            }
          }

          // Update selectedObject with the new position
          // CRITICAL: Merge from state so referenceUrl and other editor-only props are preserved
          if (dragged && setSelectedObject) {
            const fromState =
              (mainState || []).find((el) => el.id === id) ||
              (offCanvasState || []).find((el) => el.id === id) ||
              dragged;
            const updatedImageForSelection = {
              ...fromState,
              ...dragged,
              x: constrainedX,
              y: constrainedY,
              width: finalWidth,
              height: finalHeight,
              type: "Image",
              _isInOffCanvasDuringDrag: undefined,
              _previewTargetStage: undefined,
              _isDragging: undefined,
            };
            setSelectedObject(updatedImageForSelection);
            syncSelectedObjectsForId(updatedImageForSelection);
            // CRITICAL: Also update setSelectedImage so ImageEditor shows updated coordinates
            if (setSelectedImage) {
              setSelectedImage(updatedImageForSelection);
            }
          }
        } else if (typeof handleImageDragEnd === "function") {
          // CRITICAL: Mark pending changes so endElementEditing knows there was a change
          // This ensures undo button becomes enabled after dragging an existing element
          if (typeof setPendingChanges === "function") {
            setPendingChanges(true);
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }
          // CRITICAL: Pass the current dragged image object to preserve all properties (rotation, scaleX, scaleY, etc.)
          // This ensures that when we update only x, y, width, height, all other properties are preserved
          // Use constrained positions for off-canvas images
          handleImageDragEnd(id, constrainedX, constrainedY, finalWidth, finalHeight, dragged);

          // Keep Konva node visually synced to finalized coords immediately.
          const stageForNodeSync =
            target === "offCanvas" ? offCanvasStage : mainStage;
          if (stageForNodeSync && typeof stageForNodeSync.findOne === "function") {
            try {
              const imageNode = stageForNodeSync.findOne(`#${id}`);
              if (imageNode && typeof imageNode.position === "function") {
                imageNode.position({
                  x: Math.round(constrainedX),
                  y: Math.round(constrainedY),
                });
                const layer = imageNode.getLayer?.();
                if (layer && typeof layer.batchDraw === "function") {
                  layer.batchDraw();
                }
              }
            } catch (_) {
              // no-op
            }
          }

          // CRITICAL: Update selectedObject and selectedImage with the new position AFTER handleImageDragEnd
          // This ensures selectedObject has the correct position and prevents React-Konva from reverting it
          // CRITICAL: Merge from state so referenceUrl and other editor-only props are preserved
          if (dragged) {
            const fromState =
              (mainState || []).find((el) => el.id === id) ||
              (offCanvasState || []).find((el) => el.id === id) ||
              dragged;
            const updatedImageForSelection = {
              ...fromState,
              ...dragged,
              x: constrainedX,
              y: constrainedY,
              width: finalWidth,
              height: finalHeight,
              type: "Image",
              _isInOffCanvasDuringDrag: undefined,
              _previewTargetStage: undefined,
              _isDragging: undefined,
            };

            // Update selection immediately with the final position
            if (setSelectedObject) {
              setSelectedObject(updatedImageForSelection);
            }
            if (setSelectedImage) {
              setSelectedImage(updatedImageForSelection);
            }
            syncSelectedObjectsForId(updatedImageForSelection);

            // CRITICAL FIX: Some stage/background click handlers may still run
            // after drag end and clear selection (especially for top-layer images).
            // Schedule a re-select on the next tick so the dragged image remains
            // selected just like non top-layer images.
            if (setSelectedObject || setSelectedImage) {
              setTimeout(() => {
                if (setSelectedObject) {
                  setSelectedObject(updatedImageForSelection);
                }
                if (setSelectedImage) {
                  setSelectedImage(updatedImageForSelection);
                }
                syncSelectedObjectsForId(updatedImageForSelection);
              }, 0);
            }
          }
        } else {
          console.error("🔴 [DRAG-END-UTILS] handleImageDragEnd is not a function!", {
            id,
            handleImageDragEndType: typeof handleImageDragEnd,
          });
        }
      } catch (e) {
        // fall back
        if (typeof saveToHistory === "function") {
          setTimeout(() => saveToHistory(), 0);
        }
      }
    } else if (type === "Rect") {
      try {
        const RECT_NODE_MIN_X = 11;
        // CRITICAL: Use currentRectangle if provided (from renderer), otherwise find from state
        // currentRectangle should have the latest properties including rotation, scaleX, scaleY, etc.
        const dragged = clearDragStageFlags(
          currentRectangle ||
            (Array.isArray(mainState) ? mainState : []).find(
              (el) => el.id === id
            ) ||
            (Array.isArray(offCanvasState) ? offCanvasState : []).find(
              (el) => el.id === id
            ),
        );
        // Determine if rectangle is from off-canvas and get the correct arrays/setters
        const isRectOffCanvas = !mainState.some((el) => el.id === id) && offCanvasState.some((el) => el.id === id);
        const rectsToUpdate = isRectOffCanvas ? offCanvasState : mainState;
        const setRectsToUpdate = isRectOffCanvas ? setOffCanvasState : setMainState;

        // CRITICAL FIX: Constrain positions if element is staying in off-canvas
        let constrainedX = finalX;
        let constrainedY = finalY;
        const isStayingInOffCanvas = isRectOffCanvas && !shouldTransferToMain;

        if (isStayingInOffCanvas && offCanvasStage && dragged) {
          // Rotation-aware clamp first: use rendered clientRect so rotated off-canvas
          // rectangles don't get over-corrected by simple width/height bbox logic.
          const offRectNode =
            typeof offCanvasStage.findOne === "function"
              ? offCanvasStage.findOne(`#${id}`)
              : null;
          if (offRectNode) {
            const clamped = clampWithRenderedClientRect({
              stage: offCanvasStage,
              node: offRectNode,
              candidateX: constrainedX,
              candidateY: constrainedY,
              avoidOffCanvasMainColumn: true,
              edgeInsetPx: getTransformerEdgeInsetForType("Rect"),
            });
            constrainedX = clamped.x;
            constrainedY = clamped.y;
          } else {
            const { constrainedX: cxR, constrainedY: cyR } = constrainOffCanvasPosition(
              dragged,
              "Rect",
              constrainedX,
              constrainedY,
              offCanvasStage,
              null,
              TRANSFORMER_ANCHOR_BUFFER_PX,
            );
            constrainedX = cxR;
            constrainedY = cyR;
          }

          if (finalX !== constrainedX || finalY !== constrainedY) {
            console.warn(
              "🔧 [DRAG-END] Constraining rectangle position in off-canvas to stay within bounds",
              {
                id,
                originalX: finalX,
                originalY: finalY,
                constrainedX,
                constrainedY,
              }
            );
          }
        }

        // Keep main-canvas rectangle node origin aligned with the expected left inset.
        // Very wide rects can legally pass rendered client-rect clamp at x=10 due stroke;
        // enforce node-origin minimum so drag-end matches normal-width rectangle behavior.
        if (!isRectOffCanvas) {
          constrainedX = Math.max(RECT_NODE_MIN_X, constrainedX);
        }

        if (isStayingInOffCanvas && dragged) {
          // Off-canvas rectangle: update offCanvasState directly (mirrors Circle approach)
          // This is the critical fix: handleRectangleDragEnd only updates main setRectangles,
          // so off-canvas drags snapped back. Use setOffCanvasState instead.
          const updatedRects = rectsToUpdate.map((rect) =>
            rect.id === id
              ? { ...dragged, x: constrainedX, y: constrainedY }
              : rect
          );

          setRectsToUpdate(updatedRects);

          if (typeof setPendingChanges === "function") {
            setPendingChanges(true);
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }

          if (saveToHistory) {
            setTimeout(() => {
              // Index 9 is currentOffCanvasRectanglesOverride
              saveToHistory("rectangle_drag_end", null, null, null, null, null, null, null, null, updatedRects);
            }, 0);
          }
        } else if (typeof handleRectangleDragEnd === "function") {
          // Main canvas rectangle: use the existing handler
          if (typeof setPendingChanges === "function") {
            setPendingChanges(true);
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }
          // CRITICAL: Pass the current dragged rectangle object to preserve all properties (rotation, scaleX, scaleY, etc.)
          handleRectangleDragEnd(id, constrainedX, constrainedY, dragged);
        }

        // CRITICAL FIX: Keep selection in sync with final clamped coordinates immediately.
        // Without this, selectedObject can keep pre-clamp x/y until unselect/reselect.
        if (dragged && typeof setSelectedObject === "function") {
          const finalRectForSelection = {
            ...dragged,
            x: constrainedX,
            y: constrainedY,
            type: "Rect",
            _isInOffCanvasDuringDrag: undefined,
            _previewTargetStage: undefined,
            _isDragging: undefined,
          };
          setSelectedObject(finalRectForSelection);
          syncSelectedObjectsForId({
            x: constrainedX,
            y: constrainedY,
            type: "Rect",
            _isInOffCanvasDuringDrag: undefined,
            _previewTargetStage: undefined,
            _isDragging: undefined,
          });
        }
      } catch (e) {
        // fall back
        if (typeof saveToHistory === "function") {
          setTimeout(() => saveToHistory(), 0);
        }
      }
    } else if (type === "Circle") {
      try {
        // CRITICAL: Use currentCircle if provided (from renderer), otherwise find from state
        // currentCircle should have the latest properties including rotation, scaleX, scaleY, etc.
        const dragged = clearDragStageFlags(
          currentCircle ||
            (Array.isArray(mainState) ? mainState : []).find(
              (el) => el.id === id
            ) ||
            (Array.isArray(offCanvasState) ? offCanvasState : []).find(
              (el) => el.id === id
            ),
        );
        // Determine if circle is from off-canvas and get the correct arrays
        const isCircleOffCanvas = !mainState.some((el) => el.id === id) && offCanvasState.some((el) => el.id === id);
        const circlesToUpdate = isCircleOffCanvas ? offCanvasState : mainState;
        const setCirclesToUpdate = isCircleOffCanvas ? setOffCanvasState : setMainState;

        // CRITICAL FIX: Constrain positions if circle is staying in off-canvas
        let constrainedX = finalX;
        let constrainedY = finalY;
        // Check if circle is staying in off-canvas (not transferring to main)
        const isStayingInOffCanvas = isCircleOffCanvas && !shouldTransferToMain;

        if (isStayingInOffCanvas && offCanvasStage && dragged) {
          const { constrainedX: cxC, constrainedY: cyC } = constrainOffCanvasPosition(
            dragged,
            "Circle",
            constrainedX,
            constrainedY,
            offCanvasStage,
            null,
            getTransformerEdgeInsetForType("Circle"),
          );
          constrainedX = cxC;
          constrainedY = cyC;

          if (finalX !== constrainedX || finalY !== constrainedY) {
            console.warn(
              "🔧 [DRAG-END] Constraining circle position in off-canvas to stay within bounds",
              {
                id,
                originalX: finalX,
                originalY: finalY,
                constrainedX,
                constrainedY,
              }
            );
          }
        }

        if (dragged) {
          // Update the correct array (main or off-canvas) with the new position
          // Preserve all properties from dragged circle (rotation, scaleX, scaleY, etc.)
          const updatedCircles = circlesToUpdate.map((circle) =>
            circle.id === id
              ? {
                ...dragged, // Preserve all existing properties (rotation, scaleX, scaleY, etc.)
                x: constrainedX,
                y: constrainedY,
              }
              : circle
          );

          // Update state
          setCirclesToUpdate(updatedCircles);

          // Keep selection in sync with the final clamped coordinates.
          // Without this, repeated drags can reuse stale pre-clamp x/y.
          if (typeof setSelectedObject === "function") {
            const finalCircleForSelection = {
              ...dragged,
              x: constrainedX,
              y: constrainedY,
              type: "Circle",
              _isInOffCanvasDuringDrag: undefined,
              _previewTargetStage: undefined,
              _isDragging: undefined,
            };
            setSelectedObject(finalCircleForSelection);
            syncSelectedObjectsForId({
              x: constrainedX,
              y: constrainedY,
              type: "Circle",
              _isInOffCanvasDuringDrag: undefined,
              _previewTargetStage: undefined,
              _isDragging: undefined,
            });
          }

          // CRITICAL: Mark pending changes so endElementEditing knows there was a change
          // This ensures undo button becomes enabled after dragging an existing element
          if (typeof setPendingChanges === "function") {
            setPendingChanges(true);
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }

          // Save to history with the updated circles array as override
          // Use the appropriate override parameter based on isCircleOffCanvas
          if (saveToHistory) {
            setTimeout(() => {
              if (isCircleOffCanvas) {
                // Index 11 is currentOffCanvasCirclesOverride
                saveToHistory("circle_drag_end", null, null, null, null, null, null, null, null, null, null, updatedCircles);
              } else {
                // Index 10 is currentCirclesOverride
                saveToHistory("circle_drag_end", null, null, null, null, null, null, null, null, null, updatedCircles);
              }
            }, 0);
          }
        }
      } catch (e) {
        // fall back
        if (typeof saveToHistory === "function") {
          setTimeout(() => saveToHistory(), 0);
        }
      }
    } else if (type === "Arrow") {
      try {
        // CRITICAL: Use currentArrow if provided (from renderer), otherwise find from state
        // currentArrow should have the latest properties including rotation, scaleX, scaleY, etc.
        const dragged = clearDragStageFlags(
          currentArrow ||
            (Array.isArray(mainState) ? mainState : []).find(
              (el) => el.id === id
            ) ||
            (Array.isArray(offCanvasState) ? offCanvasState : []).find(
              (el) => el.id === id
            ),
        );

        // Start from same-canvas normalized position (includes main-canvas clamping).
        let constrainedX = finalX;
        let constrainedY = finalY;
        const isArrowOffCanvas = !mainState.some((el) => el.id === id) && offCanvasState.some((el) => el.id === id);
        const isStayingInOffCanvas = isArrowOffCanvas && !shouldTransferToMain;

        if (isStayingInOffCanvas && offCanvasStage && dragged) {
          const { constrainedX: constrainedXResult, constrainedY: constrainedYResult } = constrainOffCanvasPosition(
            dragged,
            "Arrow",
            finalX,
            finalY,
            offCanvasStage,
            null,
            getTransformerEdgeInsetForType("Arrow"),
          );
          constrainedX = constrainedXResult;
          constrainedY = constrainedYResult;

          if (finalX !== constrainedX || finalY !== constrainedY) {
            console.warn(
              "🔧 [DRAG-END] Constraining arrow position in off-canvas to stay within bounds",
              {
                id,
                originalX: finalX,
                originalY: finalY,
                constrainedX,
                constrainedY,
              }
            );
          }
        }

        if (typeof handleArrowDragEnd === "function") {
          // CRITICAL: Mark pending changes so endElementEditing knows there was a change
          // This ensures undo button becomes enabled after dragging an existing element
          if (typeof setPendingChanges === "function") {
            setPendingChanges(true);
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }
          // CRITICAL: Pass the current dragged arrow object to preserve all properties (rotation, scaleX, scaleY, etc.)
          // This ensures that when we update only x, y, all other properties are preserved
          handleArrowDragEnd(id, constrainedX, constrainedY, dragged);
        }
      } catch (e) {
        // fall back
        if (typeof saveToHistory === "function") {
          setTimeout(() => saveToHistory(), 0);
        }
      }
    } else if (type === "DoubleSidedArrow") {
      try {
        // CRITICAL: Use currentDoubleSidedArrow if provided (from renderer), otherwise find from state
        // currentDoubleSidedArrow should have the latest properties including rotation, scaleX, scaleY, etc.
        const dragged = clearDragStageFlags(
          currentDoubleSidedArrow ||
            (Array.isArray(mainState) ? mainState : []).find(
              (el) => el.id === id
            ) ||
            (Array.isArray(offCanvasState) ? offCanvasState : []).find(
              (el) => el.id === id
            ),
        );

        // Start from same-canvas normalized position (includes main-canvas clamping).
        let constrainedX = finalX;
        let constrainedY = finalY;
        const isDoubleSidedArrowOffCanvas = !mainState.some((el) => el.id === id) && offCanvasState.some((el) => el.id === id);
        const isStayingInOffCanvas = isDoubleSidedArrowOffCanvas && !shouldTransferToMain;

        if (isStayingInOffCanvas && offCanvasStage && dragged) {
          const { constrainedX: constrainedXResult, constrainedY: constrainedYResult } = constrainOffCanvasPosition(
            dragged,
            "DoubleSidedArrow",
            finalX,
            finalY,
            offCanvasStage,
            null,
            getTransformerEdgeInsetForType("DoubleSidedArrow"),
          );
          constrainedX = constrainedXResult;
          constrainedY = constrainedYResult;

          if (finalX !== constrainedX || finalY !== constrainedY) {
            console.warn(
              "🔧 [DRAG-END] Constraining double-sided arrow position in off-canvas to stay within bounds",
              {
                id,
                originalX: finalX,
                originalY: finalY,
                constrainedX,
                constrainedY,
              }
            );
          }
        }

        if (typeof handleDoubleSidedArrowDragEnd === "function") {
          // CRITICAL: Mark pending changes so endElementEditing knows there was a change
          // This ensures undo button becomes enabled after dragging an existing element
          if (typeof setPendingChanges === "function") {
            setPendingChanges(true);
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }
          // CRITICAL: Pass the current dragged arrow object to preserve all properties (rotation, scaleX, scaleY, etc.)
          // This ensures that when we update only x, y, all other properties are preserved
          handleDoubleSidedArrowDragEnd(id, constrainedX, constrainedY, dragged);
        }
      } catch (e) {
        // fall back
        if (typeof saveToHistory === "function") {
          setTimeout(() => saveToHistory(), 0);
        }
      }
    } else if (type === "Line") {
      try {
        // CRITICAL: Use currentLine if provided (from renderer), otherwise find from state
        // currentLine should have the latest properties including rotation, scaleX, scaleY, etc.
        const dragged = clearDragStageFlags(
          currentLine ||
            (Array.isArray(mainState) ? mainState : []).find(
              (el) => el.id === id
            ) ||
            (Array.isArray(offCanvasState) ? offCanvasState : []).find(
              (el) => el.id === id
            ),
        );

        // Start from same-canvas normalized position (includes main-canvas clamping).
        let constrainedX = finalX;
        let constrainedY = finalY;
        const isLineOffCanvas = !mainState.some((el) => el.id === id) && offCanvasState.some((el) => el.id === id);
        const isStayingInOffCanvas = isLineOffCanvas && !shouldTransferToMain;

        if (isStayingInOffCanvas && offCanvasStage && dragged) {
          const { constrainedX: constrainedXResult, constrainedY: constrainedYResult } = constrainOffCanvasPosition(
            dragged,
            "Line",
            finalX,
            finalY,
            offCanvasStage,
            null,
            getTransformerEdgeInsetForType("Line"),
          );
          constrainedX = constrainedXResult;
          constrainedY = constrainedYResult;

          if (finalX !== constrainedX || finalY !== constrainedY) {
            console.warn(
              "🔧 [DRAG-END] Constraining line position in off-canvas to stay within bounds",
              {
                id,
                originalX: finalX,
                originalY: finalY,
                constrainedX,
                constrainedY,
              }
            );
          }
        }

        if (typeof handleLineDragEnd === "function") {
          // CRITICAL: Mark pending changes so endElementEditing knows there was a change
          // This ensures undo button becomes enabled after dragging an existing element
          if (typeof setPendingChanges === "function") {
            setPendingChanges(true);
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }
          // CRITICAL: Pass the current dragged line object to preserve all properties (rotation, scaleX, scaleY, etc.)
          // This ensures that when we update only x, y, all other properties are preserved
          // Note: handleLineDragEnd will update selectedObject internally if setSelectedObject was provided to createHandleLineDragEnd
          handleLineDragEnd(id, constrainedX, constrainedY, dragged);
        }
      } catch (e) {
        // fall back
        if (typeof saveToHistory === "function") {
          setTimeout(() => saveToHistory(), 0);
        }
      }
    } else if (type === "Text") {
      try {

        // CRITICAL: Use currentText if provided (from renderer), otherwise find from state
        // currentText should have the latest properties including rotation, scaleX, scaleY, etc.
        const dragged = clearDragStageFlags(
          currentText ||
            (Array.isArray(mainState) ? mainState : []).find(
              (el) => el.id === id
            ) ||
            (Array.isArray(offCanvasState) ? offCanvasState : []).find(
              (el) => el.id === id
            ),
        );

        if (!dragged) {
          console.warn("🔴 [TEXT-DRAG-END] Text element not found", { id, mainStateCount: mainState?.length, offCanvasStateCount: offCanvasState?.length });
          return;
        }

        // Determine which canvas the text is on and update the correct state
        // This is critical for handling drags within the same canvas (non-transfer cases)
        // Note: This else branch only executes for non-transfer cases (element stays on same canvas)
        const isTextOffCanvas = !mainState.some((el) => el.id === id) && offCanvasState.some((el) => el.id === id);
        if (typeof handleTextDragEnd === "function" && !isTextOffCanvas) {
          // CRITICAL: Sync Konva node to final position so it doesn't stay stuck at boundary after main→off→main.
          // (We commit finalX/finalY to state; the node may still be at the boundary if Konva drag didn't update it.)
          const mainStageForSync = (stageRef && typeof stageRef.current !== "undefined") ? stageRef.current : stageRef;
          if (mainStageForSync && typeof mainStageForSync.findOne === "function") {
            try {
              const textNode = mainStageForSync.findOne(`#${id}`);
              if (textNode && typeof textNode.position === "function") {
                const roundedX = Math.round(finalX);
                const roundedY = Math.round(finalY);
                textNode.position({ x: roundedX, y: roundedY });
                const layer = textNode.getLayer();
                if (layer && typeof layer.batchDraw === "function") layer.batchDraw();
              }
            } catch (_) { /* ignore */ }
          }
          // CRITICAL: Mark pending changes so endElementEditing knows there was a change
          // This ensures undo button becomes enabled after dragging an existing element
          if (typeof setPendingChanges === "function") {
            setPendingChanges(true);
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }
          // Main canvas element - use handleTextDragEnd
          // CRITICAL: Pass the current dragged text object to preserve all properties (rotation, scaleX, scaleY, etc.)
          // This ensures that when we update only x, y, all other properties are preserved
          // Use finalX and finalY (which account for coordinate conversion) instead of newX/newY
          // CRITICAL FIX: Round coordinates to prevent sub-pixel positioning errors
          handleTextDragEnd(
            id,
            Math.round(finalX),
            Math.round(finalY),
            mouseClientX,
            mouseClientY,
            dragged
          );
        } else if (isTextOffCanvas) {
          // Off-canvas element - update state directly to ensure it doesn't vanish
          // This handles the case where element stays on off-canvas but position changes
          // CRITICAL: handleTextDragEnd is bound to main canvas state, so we must update off-canvas state directly

          // CRITICAL FIX: Round coordinates before constraining to prevent sub-pixel positioning errors
          const roundedFinalX = Math.round(finalX);
          const roundedFinalY = Math.round(finalY);

          // finalX/finalY were already normalized for off-canvas above (including
          // rendered client-rect clamp). Re-applying a second text-specific clamp
          // here can feel harsher than rectangles near bottom edge.
          const constrainedX = roundedFinalX;
          const constrainedY = roundedFinalY;


          const updatedTexts = offCanvasState.map((text) =>
            text.id === id
              ? {
                ...dragged, // Preserve all existing properties (rotation, scaleX, scaleY, etc.)
                x: constrainedX, // Use constrained coordinates
                y: constrainedY, // Use constrained coordinates
                _lastUpdated: Date.now(),
                _isInOffCanvasDuringDrag: undefined, // Clear the drag flag
                _previewTargetStage: undefined,
                _isDragging: undefined,
              }
              : text
          );

          // Update off-canvas state
          setOffCanvasState(updatedTexts);
          // Keep Konva node in sync immediately so selected text does not visually
          // remain at its drag-preview position until the next selection cycle.
          if (offCanvasStage && typeof offCanvasStage.findOne === "function") {
            try {
              const offCanvasTextNode = offCanvasStage.findOne(`#${id}`);
              if (offCanvasTextNode && typeof offCanvasTextNode.position === "function") {
                offCanvasTextNode.position({
                  x: Math.round(constrainedX),
                  y: Math.round(constrainedY),
                });
                const offLayer = offCanvasTextNode.getLayer?.();
                if (offLayer && typeof offLayer.batchDraw === "function") {
                  offLayer.batchDraw();
                }
              }
            } catch (_) {
              // no-op
            }
          }

          // Update selectedObject with the final converted coordinates
          const finalCoords = {
            x: constrainedX,
            y: constrainedY,
            _isInOffCanvasDuringDrag: undefined,
            _previewTargetStage: undefined,
            _isDragging: undefined,
          };
          setSelectedObject({ ...dragged, ...finalCoords, type });
          if (typeof setSelectedText === "function") {
            setSelectedText((prev) =>
              prev?.id === id ? { ...prev, ...dragged, ...finalCoords, type } : prev
            );
          }

          // Save to history with the updated off-canvas texts array as override
          if (saveToHistory) {
            setTimeout(() => {
              // Index 15 is currentOffCanvasTextsOverride
              saveToHistory("text_drag_end", null, null, null, null, null, null, null, null, null, null, null, null, null, null, updatedTexts);
            }, 0);
          }
          // CRITICAL: Mark pending changes so endElementEditing knows there was a change
          // This ensures undo button becomes enabled after dragging an existing element
          if (typeof setPendingChanges === "function") {
            setPendingChanges(true);
            if (pendingChangesRef) {
              pendingChangesRef.current = true;
            }
          }
        } else {
          console.warn("🔴 [TEXT-DRAG-END] handleTextDragEnd is not a function", {
            id,
            type: typeof handleTextDragEnd,
            handleTextDragEnd,
          });
        }
      } catch (e) {
        // fall back
        if (typeof saveToHistory === "function") {
          setTimeout(() => saveToHistory(), 0);
        }
      }
    } else {
      if (typeof saveToHistory === "function") {
        setTimeout(() => saveToHistory(), 0);
      }
    }
  }

  // Note: For transfer cases, we already cleared selection in the transfer branches above
  // For non-transfer cases, setSelectedObject is called in the "else" branch above with converted coordinates
  // No need to set it again here for transfer cases since we explicitly cleared it

  // Don't save to history for images since handleImageDragEnd will handle that
  // Don't save to history for rectangles since handleRectangleDragEnd will handle that
  // Don't save to history for circles since handleCircleDragEnd will handle that
  // Don't save to history for arrows since handleArrowDragEnd will handle that
  // Don't save to history for double-sided arrows since handleDoubleSidedArrowDragEnd will handle that
  // Don't save to history for lines since handleLineDragEnd will handle that
  // Don't save to history for text since handleTextDragEnd will handle that
  // Also skip for transfer cases since we already saved in the transfer branches
  if (
    type !== "Image" &&
    type !== "Rect" &&
    type !== "Circle" &&
    type !== "Arrow" &&
    type !== "DoubleSidedArrow" &&
    type !== "Line" &&
    type !== "Text" &&
    !(sourceStageForBBox === mainStage && target === "offCanvas") &&
    !(sourceStageForBBox === offCanvasStage && target === "main")
  ) {
    saveToHistory();
  }

  // Safety check: Ensure the element wasn't lost during transfer
  setTimeout(() => {
    const elementExistsInMain = mainState.some((el) => el.id === id);
    const elementExistsInOffCanvas = offCanvasState.some((el) => el.id === id);

    if (!elementExistsInMain && !elementExistsInOffCanvas) {
      // Recovery: Keep the element in its original canvas
      if (sourceStageForBBox === mainStage) {
        setMainState((prev) => {
          const elementExists = prev.some((el) => el.id === id);
          if (!elementExists) {
            return [...prev, { ...draggedElement, x: newX, y: newY, _isInOffCanvasDuringDrag: undefined }];
          }
          return prev;
        });
      } else {
        setOffCanvasState((prev) => {
          const elementExists = prev.some((el) => el.id === id);
          if (!elementExists) {
            return [...prev, { ...draggedElement, x: newX, y: newY, _isInOffCanvasDuringDrag: undefined }];
          }
          return prev;
        });
      }
    }
  }, 100);

  // Additional safety check specifically for arrows with waypoints
  if (type === "Arrow" || type === "Line" || type === "DoubleSidedArrow") {
    const points = draggedElement.points || [0, 0, 0, 0];
    const hasMultipleWaypoints = points.length > 4;

    if (hasMultipleWaypoints) {
      setTimeout(() => {
        const elementExistsInMain = mainState.some((el) => el.id === id);
        const elementExistsInOffCanvas = offCanvasState.some(
          (el) => el.id === id
        );

        if (!elementExistsInMain && !elementExistsInOffCanvas) {
          // Force recovery to the source canvas
          if (sourceStageForBBox === mainStage) {
            setMainState((prev) => [
              ...prev,
              { ...draggedElement, x: newX, y: newY, _isInOffCanvasDuringDrag: undefined },
            ]);
          } else {
            setOffCanvasState((prev) => [
              ...prev,
              { ...draggedElement, x: newX, y: newY, _isInOffCanvasDuringDrag: undefined },
            ]);
          }
        }
      }, 150);
    }
  }
};
